/**
 * Settings.
 *
 * Opens on Profile, because that is what people come here for; the knobs come
 * after. Every change saves immediately - there is no Save button to forget.
 */

import { createContext, useContext, useEffect, useId, useState, type ReactNode } from 'react';
import { useBackdropClose } from '@/lib/backdrop';
import { createPortal } from 'react-dom';
import { api, endpoints, serverUrl, setServerUrl, setToken, DEFAULT_SERVER } from '@/lib/api';
import { cn } from '@/lib/cn';
import { FONTS } from '@/lib/fonts';
import { useSession } from '@/stores/session';
import { useNarrow } from '@/components/TabBar';
import { useQuery } from '@tanstack/react-query';
import { listDevices, useVoice } from '@/stores/voice';
import { MicCheck } from '@/components/MicCheck';
import { NotifyDoctor } from '@/components/NotifyDoctor';
import { PrefsBackup } from '@/components/PrefsBackup';
import { MyReports, ReportProblem } from '@/components/Safety';
import { PushToTalk } from '@/components/PushToTalk';
import { Select } from '@/components/Select';
import { SOUND_NAMES, isSampled, preview } from '@/lib/sound';
import { bridge, isDesktop, type UpdateState } from '@/lib/bridge';
import * as Icons from '@/icons';
import { EditProfile } from '@/components/EditProfile';
import { Keybinds } from '@/components/Keybinds';
import { FRAMES, FrameRing } from '@/components/Frames';
import { Icon } from '@/icons/Icon';
import { Avatar } from '@/components/Avatar';
import { ChatPreview } from '@/components/ChatPreview';
import { ProfilePreview } from '@/components/ProfilePreview';
import { AccountStanding } from '@/components/Standing';
import { CropPicture, type Shape } from '@/components/CropPicture';
import { UserName } from '@/components/user';
import { EmailCard, TwoFactorCard } from '@/components/Security';
import { ConnectionsTab } from '@/components/Connections';
import { ApiKeysCard } from '@/components/ApiKeys';
import { Switch } from '@/components/Switch';
import { ActivityTab, AddonsTab, ChatTab, LanguageTab, ThemesPane, TypographyPane, VoiceDockSettings, VoiceProcessing } from '@/components/SettingsMore';
import { useT } from '@/lib/i18n';
import { useRoute, go } from '@/lib/hash';
import {
  Button, Card, CardHead, Field, Input, Tag, Toggle, useConfirm, useToast
} from '@/components/ui';

type Tab =
  | 'account' | 'profile' | 'games' | 'connections' | 'premium'
  | 'privacy' | 'voice' | 'notifications'
  | 'appearance' | 'text' | 'keys' | 'advanced'
  | 'chat' | 'activity' | 'language' | 'addons';

/*
 * The sections, in the order the reference groups them: who you are first,
 * then how you are reached, then how the app looks. Grouped rather than one
 * flat list, because twelve unlabelled rows is a wall.
 */
/**
 * One section in the sidebar, and the sections it owns.
 *
 * Typed rather than inferred from `as const`: a tuple of eight differently
 * shaped literals is not something TypeScript can flatMap over usefully, and
 * the shape is the point here anyway.
 */
type NavItem = {
  id: string;
  label: string;
  icon: (p: Icons.IconProps) => React.ReactElement;
  /**
   * Its own sections, shown under it while it is the one you are on.
   *
   * `desktop` marks one that only exists in the desktop build. A browser
   * cannot put a call bar over somebody's game, so listing it there is a
   * row that opens an empty page - worse than not offering it at all.
   */
  sub?: { id: string; label: string; desktop?: boolean }[];
};
type NavGroup = { label: string; items: NavItem[] };

const GROUPS: NavGroup[] = [
  {
    label: 'User settings',
    items: [
      { id: 'account', label: 'My account', icon: Icons.Profile },
      { id: 'profile', label: 'Profile', icon: Icons.Edit },
      { id: 'privacy', label: 'Privacy & safety', icon: Icons.Shield },
      { id: 'activity', label: 'Activity privacy', icon: Icons.Users },
      { id: 'games', label: 'Games', icon: Icons.Grid },
      { id: 'connections', label: 'Connections', icon: Icons.Plug },
      { id: 'premium', label: 'Membership', icon: Icons.Star }
    ]
  },
  {
    label: 'App settings',
    items: [
      /*
       * Sub-sections in the sidebar rather than a second row of tabs inside
       * the page.
       *
       * Appearance already had three panes and Voice had four, reachable
       * only after you had opened the section and found a second navigation
       * inside it - so the way to the thing you wanted was hidden behind the
       * thing you did not. They are part of the one list now: the section
       * opens its own when it is the one you are on, and closes it when it
       * is not, so the list never becomes the whole page.
       */
      { id: 'appearance', label: 'Appearance', icon: Icons.Palette, sub: [
        { id: 'themes', label: 'Themes' },
        { id: 'display', label: 'Display' },
        { id: 'typography', label: 'Size & font' }
      ] },
      { id: 'text', label: 'Accessibility', icon: Icons.Type },
      { id: 'voice', label: 'Voice & video', icon: Icons.Headset, sub: [
        { id: 'devices', label: 'Devices' },
        { id: 'processing', label: 'Processing' },
        { id: 'dock', label: 'The call dock', desktop: true }
      ] },
      { id: 'chat', label: 'Chat', icon: Icons.Smile },
      { id: 'notifications', label: 'Notifications', icon: Icons.Bell },
      { id: 'keys', label: 'Keybinds', icon: Icons.Command },
      { id: 'language', label: 'Language & time', icon: Icons.Globe },
      { id: 'addons', label: 'Addons', icon: Icons.Cube },
      { id: 'advanced', label: 'Advanced', icon: Icons.Sliders }
    ]
  }
];

/* ----------------------------------------------------------- profile */

function ProfileTab() {
  const { me, patchMe } = useSession();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  if (!me) return null;

  const perks = me.perks;
  const vip = perks.vip;

  async function save(patch: Record<string, unknown>, note: string) {
    setBusy(true);
    try {
      await patchMe(patch);
      toast(note, 'good');
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  /*
   * The picture the file picker handed over, waiting to be framed.
   *
   * Nothing is uploaded until the crop is confirmed, so cancelling costs a
   * file read and no bytes over the wire.
   */
  const [cropping, setCropping] = useState<{ file: File; shapes: Shape[] } | null>(null);

  /** Send one already-framed blob and return the url it landed at. */
  async function put(blob: Blob, name: string, kind: string): Promise<string> {
    const res = await fetch(serverUrl() + '/api/files', {
      method: 'POST',
      headers: {
        Authorization: 'Bearer ' + (localStorage.getItem('pogu.token') ?? ''),
        'Content-Type': blob.type || 'image/webp',
        'X-File-Type': blob.type || 'image/webp',
        'X-File-Name': encodeURIComponent(name),
        'X-File-Kind': kind
      },
      body: blob
    });
    const data = await res.json() as { file?: { url: string }; error?: string };
    if (!data.file) throw new Error(data.error ?? 'Upload failed');
    return data.file.url;
  }

  /** What came back from the cropper, saved in one go. */
  async function saveCrops(out: { shape: Shape; blob: Blob }[]) {
    setBusy(true);
    try {
      const patch: Record<string, string> = {};
      for (const one of out) {
        const url = await put(one.blob, one.shape + '.webp', one.shape === 'avatar' ? 'avatar' : 'banner');
        if (one.shape === 'avatar') patch.avatarUrl = url;
        if (one.shape === 'banner') patch.banner = url;
        if (one.shape === 'banner-tall') patch.bannerTall = url;
      }
      await patchMe(patch);
      toast(out[0]?.shape === 'avatar' ? 'Picture updated' : 'Banner updated', 'good');
      setCropping(null);
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  /** Checks what it can before opening the cropper, so nothing is framed and then refused. */
  function pick(file: File, kind: 'avatar' | 'banner') {
    if (/gif$/i.test(file.type) && !(kind === 'avatar' ? perks.animatedAvatar : perks.animatedBanner)) {
      toast('Animated pictures are a VIP and Premium perk', 'warn');
      return;
    }
    const cap = perks.limits?.upload ?? 25 * 1024 * 1024;
    if (file.size > cap) {
      toast('Keep it under ' + Math.round(cap / 1048576) + ' MB', 'warn');
      return;
    }
    setCropping({ file, shapes: kind === 'avatar' ? ['avatar'] : ['banner', 'banner-tall'] });
  }


  return (
    <>
      {/* Framing, between the file picker and the upload. A banner is asked
          twice - wide for a computer, taller for a phone. */}
      {cropping ? (
        <CropPicture
          file={cropping.file}
          shapes={cropping.shapes}
          busy={busy}
          onCancel={() => setCropping(null)}
          onDone={(out) => void saveCrops(out)}
        />
      ) : null}

      <Card>
        <CardHead
          title="About you"
          note="Your name, bio, the games you play and your ranks - all of it, here."
        />
        {/* The whole form, on the page. It used to be a dialog opened from a
            button here, which meant your profile was set up in two places. */}
        <EditProfile inline section="about" onClose={() => {}} />
      </Card>

      <Card>
        <CardHead
          title="Profile picture"
          actions={<Tag kind={vip ? 'gold' : 'plain'}>{me.tier === 'vip' ? 'VIP MEMBER' : me.tier === 'premium' ? 'PREMIUM' : perks.staff ? 'STAFF' : 'STANDARD'}</Tag>}
        />
        <div className="flex flex-wrap items-start gap-[var(--gap)]">
          <span className="rounded-[26%] border-[3px] border-surface-3 shadow-[var(--shadow-2)]">
            <Avatar user={me} size={96} />
          </span>
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <div className="flex flex-wrap gap-2">
              <label>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) pick(f, 'avatar'); }}
                />
                <span className={cn(
                  'inline-flex h-[var(--h-md)] cursor-pointer items-center rounded-[var(--radius-s)] bg-accent px-4',
                  'text-[13.5px] font-semibold text-accent-fg transition-colors hover:bg-accent-hi'
                )}>
                  Upload a picture
                </span>
              </label>
              {me.avatarUrl ? (
                <Button kind="outline" disabled={busy} onClick={() => void save({ avatarUrl: null }, 'Picture removed')}>
                  Remove
                </Button>
              ) : null}
            </div>
            <p className="text-[12.5px] text-mute">PNG, JPG or WebP up to 8 MB. Square images look best.</p>
            <p className={cn('text-[12.5px]', vip ? 'text-good' : 'text-mute')}>
              {vip ? 'Animated GIFs are unlocked on your account.' : 'Animated GIF pictures are a VIP and Premium perk.'}
            </p>
          </div>
        </div>
      </Card>

      <Card>
        <CardHead title="Banner and colours" note="Shown behind your name on your profile" />
        <div
          className="relative h-[132px] overflow-hidden rounded-[var(--radius)] border border-line"
          style={{
            background: me.bannerColor
              ? `linear-gradient(115deg, ${me.bannerColor}, color-mix(in srgb, ${me.bannerColor} 45%, transparent))`
              : 'linear-gradient(115deg, color-mix(in srgb, var(--accent) 24%, transparent), transparent 66%), var(--bg-2)'
          }}
        >
          {me.banner ? (
            <img src={/^https?:/i.test(me.banner) ? me.banner : serverUrl() + me.banner} alt="" className="h-full w-full object-cover" />
          ) : (
            <span className="grid h-full place-items-center text-[12.5px] text-mute">
              No banner yet — a picture or a colour both work
            </span>
          )}
        </div>
        <div className="mt-[var(--gap)] flex flex-wrap gap-2">
          <label>
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) pick(f, 'banner'); }}
            />
            <span className="inline-flex h-[var(--h-md)] cursor-pointer items-center rounded-[var(--radius-s)] bg-accent px-4 text-[13.5px] font-semibold text-accent-fg transition-colors hover:bg-accent-hi">
              Upload a banner
            </span>
          </label>
          {me.banner ? (
            <Button kind="outline" disabled={busy} onClick={() => void save({ banner: null }, 'Banner removed')}>Remove</Button>
          ) : null}
          <ColorPick
            label="Banner colour"
            value={me.bannerColor ?? '#2a3140'}
            onApply={(v) => void save({ bannerColor: v, banner: null }, 'Banner colour set')}
            onClear={() => void save({ bannerColor: null }, 'Banner colour cleared')}
          />
          <ColorPick
            label="Profile colour"
            value={me.accent ?? '#838ea2'}
            disabled={!vip}
            onApply={(v) => void save({ accent: v }, 'Profile colour set')}
            onClear={() => void save({ accent: null }, 'Profile colour cleared')}
          />
        </div>
      </Card>

      <NameColourCard />

      <BundleCard me={me} save={save} />

      {/*
        The profile page frame, the profile effect and the banner effect are
        gone.

        Three separate things drawing over the same card: a border round the
        whole profile, something drifting across it, and something moving
        behind the name. Each was fine on its own and together they made a
        profile you could not read - and the frame in particular set
        position: relative on the card, which is what dropped somebody's
        profile card 1,370px down the page when they wore one.

        The picture frame below stays. That one is round the avatar, which is
        a small thing in a corner rather than a layer over everything.
      */}

      <Card>
        <CardHead
          title="Profile frame"
          note="Drawn rather than shipped as artwork, so they stay sharp and weigh nothing"
        />
        <div className="grid gap-[calc(var(--gap)*.7)] [grid-template-columns:repeat(auto-fill,minmax(104px,1fr))]">
          {FRAMES.map((f) => (
            <button
              key={f.id}
              type="button"
              title={f.desc}
              onClick={() => void save({ frame: f.id }, f.id === 'none' ? 'Frame removed' : f.name + ' frame applied')}
              className={cn(
                'group relative flex flex-col items-center gap-2 rounded-[var(--radius)] border px-2 pt-4 pb-2.5 transition-colors',
                me.frame === f.id
                  ? 'border-accent bg-[color-mix(in_srgb,var(--accent)_12%,var(--bg-1))] text-ink'
                  : 'border-line-soft bg-surface-1 text-dim hover:border-line hover:bg-surface-2 hover:text-ink'
              )}
            >
              {/* The real thing on your real picture, so what you see here is
                  what everyone else sees. */}
              <span className="relative inline-block leading-[0]">
                <Avatar user={me} size={44} />
                <FrameRing frame={f.id} size={44} />
              </span>

              <span className="flex items-center gap-1 text-[11.5px] font-bold">
                {f.name}
                {f.animated ? (
                  <Icon name="sparkle" size={10} className="text-accent-hi" />
                ) : null}
              </span>

              {me.frame === f.id ? (
                <span className="absolute top-1.5 right-1.5 grid h-4 w-4 place-items-center rounded-full bg-accent text-accent-fg">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4">
                    <path d="m5 12.5 4.5 4.5L19 7" />
                  </svg>
                </span>
              ) : null}
            </button>
          ))}
        </div>
      </Card>
    </>
  );
}

function ColorPick({
  label, value, disabled, onApply, onClear
}: { label: string; value: string; disabled?: boolean; onApply: (v: string) => void; onClear: () => void }) {
  const [colour, setColour] = useState(value);
  return (
    <span className="flex items-center gap-2">
      <input
        type="color"
        value={colour}
        disabled={disabled}
        onChange={(e) => setColour(e.target.value)}
        title={label}
        className="h-[var(--h-md)] w-[52px] cursor-pointer rounded-[var(--radius-s)] border border-line bg-surface-2 p-1 disabled:cursor-not-allowed disabled:opacity-45"
      />
      <Button kind="outline" size="sm" disabled={disabled} onClick={() => onApply(colour)}>Apply</Button>
      <Button kind="ghost" size="sm" disabled={disabled} onClick={onClear}>Clear</Button>
    </span>
  );
}

/**
 * A whole collection, in one press.
 *
 * The five slots are still there below and can still be mixed however
 * somebody likes. This is for the common case: they saw a look they wanted
 * and would rather not set the same word five times.
 */
function BundleCard({ me, save }: {
  me: { tier: string; frame?: string; profileFrame?: string; bannerFx?: string; profileFx?: string };
  save: (patch: Record<string, unknown>, note: string) => void | Promise<void>;
}) {
  const catalog = useSession((s) => s.catalog);
  const bundles = catalog?.bundles ?? [];
  if (!bundles.length) return null;

  /* A collection is the picture frame now. The profile frame, the profile
     effect and the banner effect were the other three slots, and those are
     gone - three things drawing over the same card is not a look, it is a
     card you cannot read. */
  const wearing = (id: string) => me.frame === id;

  return (
    <Card>
      <CardHead title="Collections" note="A picture frame and a name colour that go together" />
      <div className="grid gap-2.5 sm:grid-cols-2">
        {bundles.map((bundle) => {
          const locked = !tierAllows(me.tier, bundle.tier);
          const on = wearing(bundle.id);
          return (
            <button
              key={bundle.id}
              type="button"
              disabled={locked}
              onClick={() => void save(
                on ? { frame: 'none' } : { frame: bundle.id },
                on ? bundle.name + ' removed' : bundle.name + ' applied'
              )}
              className={cn(
                'relative overflow-hidden rounded-[var(--radius-l)] border p-3 text-left transition-colors',
                on ? 'border-accent bg-accent-soft' : 'border-line-soft bg-surface-2 hover:border-line',
                locked && 'cursor-not-allowed opacity-45'
              )}
            >
              {/* The frame wearing itself, round a stand-in picture -
                  the only preview that says anything useful about it. */}
              <span className="relative mb-2.5 flex h-[56px] w-full items-center justify-center overflow-hidden rounded-[calc(10px*var(--radius-mul,1))] bg-[linear-gradient(120deg,var(--bg-2),var(--bg-3))]">
                <span data-frame={bundle.id} className="block h-[38px] w-[38px] rounded-full bg-surface-3" />
              </span>
              <span className="flex items-center gap-1.5">
                <span className="text-[13.5px] font-bold">{bundle.name}</span>
                {bundle.tier !== 'free' ? <Tag>{bundle.tier.toUpperCase()}</Tag> : null}
                {on ? <Tag kind="accent">ON</Tag> : null}
              </span>
              <span className="mt-0.5 block text-[11.5px] text-mute">{bundle.desc}</span>
            </button>
          );
        })}
      </div>
    </Card>
  );
}

/*
 * The twelve a name may be set in, and nothing else.
 *
 * Deliberately not the app's own typefaces. Those are chosen to survive
 * being the font a whole screen is set in; a name is one short string at
 * eighteen pixels over a banner, which is the one place a blackletter or a
 * pixel face earns its keep. Must match NAME_FONTS in server/api.js - a
 * name in a font the server refuses saves as nothing at all.
 */
const NAME_FONTS = [
  'unifraktur', 'germania', 'oi', 'bungeeinline',
  'knewave', 'silkscreen', 'jimnightshade', 'hennypenny',
  'cherrycream', 'storyscript', 'shadowsintolight', 'eduqld'
];

function NameColourCard() {
  const { me, patchMe } = useSession();
  const toast = useToast();
  const [a, setA] = useState(me?.nameColor ?? '#ff9a3c');
  const [b, setB] = useState(me?.nameColor2 ?? '#ff3ca8');
  const [gradient, setGradient] = useState(!!me?.nameColor2);
  const [animated, setAnimated] = useState(!!me?.nameAnimated);
  const [font, setFont] = useState(me?.nameFont ?? '');
  if (!me) return null;

  const vip = me.perks.nameColor;

  async function save(patch: Record<string, unknown>, note: string) {
    try { await patchMe(patch); toast(note, 'good'); }
    catch (err) { toast((err as Error).message, 'bad'); }
  }

  return (
    <Card className={cn(!vip && 'opacity-95')}>
      <CardHead title="Name colour" actions={vip ? undefined : <Tag kind="warn">VIP / PREMIUM</Tag>} />

      {/*
        Your name where your name goes.

        It used to be shown large on a chequerboard, which tells you what the
        colours are and nothing about whether they work - a name is read at
        fifteen pixels over a banner, not at forty over a test pattern.
      */}
      <ProfilePreview
        className="mb-[var(--gap)]"
        nameOverride={{ nameColor: a, nameColor2: gradient ? b : null, nameAnimated: animated, nameFont: font || null }}
      />

      {/*
        Two colours on one line, and the second only when it is doing
        something. Four separate fields for what is really "pick a colour,
        optionally fade to another" is most of why this card read as
        complicated.
      */}
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Colour" hint="Your name, everywhere it appears.">
          <input type="color" value={a} disabled={!vip} onChange={(e) => setA(e.target.value)}
            className="h-[var(--h-md)] w-[64px] cursor-pointer rounded-[var(--radius-s)] border border-line bg-surface-1 p-1 disabled:opacity-45" />
        </Field>
        {gradient ? (
          <Field label="Fades to" hint="The far end of the gradient.">
            <input type="color" value={b} disabled={!vip} onChange={(e) => setB(e.target.value)}
              className="h-[var(--h-md)] w-[64px] cursor-pointer rounded-[var(--radius-s)] border border-line bg-surface-1 p-1 disabled:opacity-45" />
          </Field>
        ) : null}
      </div>

      {/*
        The typeface, which is not a VIP thing.

        A colour says something about what you have paid for; a font says
        something about you, and there is no reason to sell it. The list is
        short and every one of them is already loaded, so a name never falls
        back to the body font halfway down a list of players.
      */}
      {/*
        Each one drawn in itself, with your own name in it.

        It was a dropdown of fifteen names, which asks somebody to know what
        Space Grotesk looks like before they can choose it - so everybody
        picked the first one or gave up. A typeface is a picture; the only
        useful way to offer one is to show it, and showing it set in the name
        it will actually be applied to is the only way to know it fits.
      */}
      <Field label="Font" hint="The typeface your name is set in, everywhere it appears.">
        <div className="grid gap-1.5 [grid-template-columns:repeat(auto-fill,minmax(148px,1fr))]">
          {[null, ...NAME_FONTS].map((id) => {
            const def = id ? FONTS.find((x) => x.id === id) : null;
            if (id && !def) return null;
            const on = (font || '') === (id ?? '');
            return (
              <button
                key={id ?? 'app'}
                type="button"
                aria-pressed={on}
                onClick={() => {
                  setFont(id ?? '');
                  void save({ nameFont: id }, id ? def!.name + ' it is' : 'Back to the app font');
                }}
                className={cn(
                  'flex min-w-0 flex-col items-start gap-0.5 rounded-[calc(11px*var(--radius-mul,1))] px-3 py-2 text-left outline-none',
                  'transition-[background-color,box-shadow] duration-[var(--t-hover)] ease-[var(--ease)]',
                  'focus-visible:shadow-[0_0_0_2px_var(--accent)]',
                  on
                    ? 'bg-accent-soft shadow-[inset_0_0_0_1px_var(--accent)]'
                    : 'bg-surface-2 shadow-[inset_0_0_0_1px_var(--line-soft)] hover:bg-surface-1'
                )}
              >
                {/*
                  Your name, in the font, on one line.

                  truncate rather than a fixed height: a long name in a wide
                  face used to run under the next card, and clipping the
                  overflow with no ellipsis made it look like the name itself
                  had been cut short.
                */}
                <span
                  className={cn('block w-full truncate text-[18px] leading-[1.35]', on ? 'text-ink' : 'text-dim', def?.single ? '' : 'font-bold')}
                  style={def ? { fontFamily: def.stack, ...(def.single ? { fontWeight: 400 } : {}) } : undefined}
                  title={me.displayName || me.username}
                >
                  {me.displayName || me.username}
                </span>
                <span className="block w-full truncate text-[11px] text-ghost">
                  {def ? def.name : 'The app font'}
                </span>
              </button>
            );
          })}
        </div>
      </Field>

      <Toggle checked={gradient} onChange={setGradient} disabled={!vip}
        label="Gradient" note="Fade between the two colours" />
      {gradient ? (
        <Toggle checked={animated} onChange={setAnimated} disabled={!vip}
          label="Animated" note="The gradient drifts across your name, on a loop" />
      ) : null}

      <div className="mt-[var(--gap)] flex flex-wrap gap-2">
        <Button kind="primary" disabled={!vip}
          onClick={() => void save({ nameColor: a, nameColor2: gradient ? b : null, nameAnimated: animated }, 'Name colour saved')}>
          Save name colour
        </Button>
        <Button kind="outline" disabled={!vip}
          onClick={() => void save({ nameColor: null, nameColor2: null, nameAnimated: false }, 'Name colour cleared')}>
          Clear
        </Button>
        {!vip ? <span className="self-center text-[12px] text-mute">Ask an admin about VIP to unlock this.</span> : null}
      </div>
    </Card>
  );
}

/* -------------------------------------------------------- appearance */

/**
 * How Pugo looks.
 *
 * The one settings screen that takes the whole modal width, because the
 * preview beside it is the point: every control here changes something you
 * cannot judge from its name, and reading "Surface contrast: 4" tells you
 * nothing about whether you want it.
 *
 * The preview is the real app under a scaled-down copy of the tokens, not a
 * picture of it - so it can never drift from what the settings actually do.
 */


type Pane = 'themes' | 'display' | 'typography';

/**
 * Appearance, which is really two screens: the controls and a live preview.
 *
 * It is used in two places and they need different shapes. Over the app as a
 * panel it owns the whole pane and scrolls its two halves independently. On
 * the settings page it sits inside a column that already scrolls, and nesting
 * a second scroller inside the first is what left the theme cards sliced off
 * at the bottom with no way to reach them. Embedded, it lets the page do the
 * scrolling and parks the preview alongside instead.
 */
function AppearanceTab({ onBack, embedded, pane: given, onPane }: {
  onBack?: () => void;
  embedded?: boolean;
  /**
   * Which sub-section is open, when somebody outside is in charge of it.
   *
   * On the settings page the sidebar now lists these, so the tab must not
   * also keep its own answer - two controls for one thing disagree the
   * moment either is used. Over the app as a floating panel there is no
   * sidebar, so it falls back to holding its own.
   */
  pane?: string;
  onPane?: (id: string) => void;
}) {
  const { settings, catalog, saveSettings } = useSession();
  const [own, setOwn] = useState<Pane>('themes');
  const pane = (given ?? own) as Pane;
  const setPane = (id: Pane) => (onPane ? onPane(id) : setOwn(id));
  if (!settings) return null;

  const set = (patch: Record<string, unknown>) => void saveSettings(patch);
  const themes = (catalog?.themes ?? []).filter((t) => t.group !== 'Legacy');
  const theme = settings.theme === 'dark' ? 'midnight' : settings.theme;
  const accent = settings.accent ?? 'theme';
  const contrast = settings.contrast ?? 3;
  const radius = settings.cornerRadius ?? 1;
  const shadow = settings.shadowDepth ?? 1;

  const PANES: { id: Pane; label: string; icon: string }[] = [
    { id: 'themes', label: 'Themes', icon: 'palette' },
    { id: 'display', label: 'Display', icon: 'layers' },
    { id: 'typography', label: 'Size & font', icon: 'hash' }
  ];

  return (
    <div className={cn(
      'flex flex-col',
      embedded ? 'gap-6 xl:flex-row xl:items-start' : 'min-h-0 flex-1 lg:flex-row'
    )}>
      <div className={cn(
        'min-w-0 flex-1',
        embedded ? '' : 'min-h-0 overflow-y-auto px-4 py-5 lg:px-8 lg:py-7'
      )}>
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="mb-3 flex items-center gap-1.5 text-[13px] font-semibold text-mute transition-colors hover:text-ink"
          >
            <Icon name="chevronL" size={15} />
            Settings
          </button>
        ) : null}
        <h1 className="text-[26px] leading-tight font-bold tracking-[-.015em]">Appearance &amp; Themes</h1>
        <p className="mt-0.5 text-[13px] text-mute">Customise how Pugo looks and feels.</p>

        <div className="mt-5 flex flex-col gap-4 lg:flex-row lg:gap-5">
          {/* the sub-sections - a scrolling strip on a phone, a column on a
              desktop, and nothing at all when the sidebar is listing them */}
          <nav className={cn(
            'no-scrollbar -mx-4 flex-none gap-1 overflow-x-auto px-4 lg:mx-0 lg:w-[150px] lg:flex-col lg:gap-0.5 lg:overflow-visible lg:px-0',
            given ? 'hidden' : 'flex'
          )}>
            {PANES.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setPane(p.id)}
                className={cn(
                  'flex flex-none items-center gap-2.5 rounded-[calc(8px*var(--radius-mul,1))] px-3 py-2 text-left text-[13px] font-medium whitespace-nowrap transition-colors',
                  pane === p.id ? 'bg-accent-soft text-ink' : 'text-mute hover:bg-surface-2 hover:text-dim'
                )}
              >
                <Icon name={p.icon} size={15} className={pane === p.id ? 'text-accent-hi' : 'opacity-60'} />
                {p.label}
              </button>
            ))}
          </nav>

          <div className="min-w-0 flex-1 rounded-[var(--radius-l)] border border-line-soft bg-surface-2 p-4 lg:p-5">
            {pane === 'themes' ? <ThemesPane /> : null}

            {pane === 'display' ? (
              <>
                <Slider
                  label="Surface contrast"
                  note="How far cards sit above the window."
                  min={1}
                  max={5}
                  step={1}
                  value={contrast}
                  display={['Lowest', 'Low', 'Default', 'Medium high', 'Highest'][contrast - 1] ?? 'Default'}
                  onChange={(v) => set({ contrast: v })}
                />

                <div className="border-b border-line-soft py-3.5">
                  <div className="text-[13.5px] font-semibold">Density</div>
                  <div className="text-[12px] text-mute">How tightly everything is packed.</div>
                  <div className="mt-2 inline-flex w-fit gap-1 rounded-[calc(10px*var(--radius-mul,1))] border border-line-soft bg-surface-1 p-1">
                    {[
                      { id: 'compact', label: 'Compact' },
                      { id: 'comfortable', label: 'Cozy' }
                    ].map((d) => (
                      <button
                        key={d.id}
                        type="button"
                        onClick={() => set({ density: d.id })}
                        className={cn(
                          'rounded-[calc(8px*var(--radius-mul,1))] px-5 py-1.5 text-[12.5px] font-semibold transition-colors',
                          (settings.density === 'compact' ? 'compact' : 'comfortable') === d.id
                            ? 'bg-accent text-accent-fg'
                            : 'text-mute hover:text-ink'
                        )}
                      >
                        {d.label}
                      </button>
                    ))}
                  </div>
                </div>

                <Slider
                  label="Corner radius"
                  note="How round corners are across the app."
                  min={0}
                  max={2}
                  step={0.1}
                  value={radius}
                  display={Math.round(12 * radius) + 'px'}
                  onChange={(v) => set({ cornerRadius: v })}
                />
                {/* Applies to every avatar in the app at once. */}
                <div className="border-b border-line-soft py-3.5">
                  <div className="text-[13.5px] font-semibold">Avatar shape</div>
                  <div className="text-[12px] text-mute">How pictures of people are cropped, everywhere.</div>
                  <div className="mt-2 inline-flex w-fit flex-wrap gap-1 rounded-[calc(10px*var(--radius-mul,1))] border border-line-soft bg-surface-1 p-1">
                    {[
                      { id: 'round', label: 'Circle', r: '50%' },
                      { id: 'soft', label: 'Rounded', r: '32%' },
                      { id: 'square', label: 'Square', r: '18%' }
                    ].map((o) => (
                      <button
                        key={o.id}
                        type="button"
                        onClick={() => set({ avatarShape: o.id })}
                        className={cn(
                          'flex items-center justify-center gap-2 rounded-[calc(8px*var(--radius-mul,1))] px-4 py-1.5 text-[12.5px] font-semibold transition-colors',
                          (settings.avatarShape ?? 'round') === o.id ? 'bg-accent text-accent-fg' : 'text-mute hover:text-ink'
                        )}
                      >
                        <span
                          className="block h-[15px] w-[15px] bg-current opacity-70"
                          style={{ borderRadius: o.r }}
                        />
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>

                <Slider
                  label="Shadow intensity"
                  note="The depth of shadows and elevation."
                  min={0}
                  max={2}
                  step={0.1}
                  value={shadow}
                  display={shadow < 0.4 ? 'None' : shadow < 0.8 ? 'Low' : shadow < 1.3 ? 'Medium' : shadow < 1.7 ? 'High' : 'Highest'}
                  onChange={(v) => set({ shadowDepth: v })}
                />
                <SRow label="Background glow" note="The soft wash of accent colour behind the app">
                  <SwitchC on={settings.illustration !== false} onChange={(v) => set({ illustration: v })} />
                </SRow>
              </>
            ) : null}

            {pane === 'typography' ? <TypographyPane /> : null}

          </div>
        </div>
      </div>

      {/* ---- the preview ---- */}
      {/*
        Pinned, and never a scrolling column of its own.

        A preview you have to scroll back to is a picture you have to
        remember. It sticks to the top of the pane while the settings move
        past it, and it holds no more than fits - overflow-y-auto here used
        to turn the one thing you were meant to be watching into a second
        thing to scroll.
      */}
      <aside className={cn(
        'flex w-full flex-none flex-col gap-3 self-start bg-surface-side p-4',
        embedded
          ? 'rounded-[var(--radius-l)] border border-line-soft xl:sticky xl:top-4 xl:w-[340px] xl:min-w-[340px] xl:p-5'
          : 'border-t border-line-soft lg:sticky lg:top-4 lg:w-[340px] lg:min-w-[340px] lg:border-t-0 lg:border-l lg:p-5'
      )}>
        <div>
          <h2 className="text-[15px] font-bold">Live preview</h2>
          <p className="text-[12px] text-mute">A real conversation, with these settings on it. Press a name to open their card.</p>
        </div>

        <ChatPreview onEmojiScale={(px) => set({ emojiScale: px })} />

        <PreviewApp />

        <div className="rounded-[var(--radius-l)] border border-line-soft bg-surface-1 p-3">
          <div className="text-[12.5px] font-bold">Theme summary</div>
          <p className="mt-0.5 text-[11.5px] leading-relaxed text-mute">
            {(themes.find((t) => t.id === theme)?.name ?? 'Graphite Green')} theme,{' '}
            {accent === 'theme' ? 'its own' : accent.charAt(0).toUpperCase() + accent.slice(1)} accent,{' '}
            {settings.density === 'compact' ? 'Compact' : 'Cozy'} density,{' '}
            {Math.round(12 * radius)}px radius.
          </p>
        </div>

        <button
          type="button"
          onClick={() => set({
            theme: 'graphite', accent: 'theme', density: 'comfortable', uiScale: 'm', fontFamily: 'inter',
            contrast: 3, cornerRadius: 1, shadowDepth: 1, illustration: true, highContrast: false
          })}
          className="flex h-[36px] items-center justify-center gap-2 rounded-[var(--radius)] border border-line bg-surface-2 text-[12.5px] font-bold text-dim transition-colors hover:border-accent-line hover:text-ink"
        >
          <Icon name="refresh" size={14} />
          Reset to defaults
        </button>
      </aside>
    </div>
  );
}

/**
 * A miniature of the app, drawn with the same tokens.
 *
 * Not a screenshot: these are the real custom properties, so whatever the
 * sliders do to the app happens here at the same moment and in the same way.
 */
function PreviewApp() {
  const me = useSession((s) => s.me);
  return (
    <div className="overflow-hidden rounded-[var(--radius-l)] border border-line-soft bg-surface-0">
      <div className="flex h-[196px]">
        {/* rail */}
        <div className="flex w-[34px] flex-none flex-col items-center gap-2.5 border-r border-line-soft bg-surface-side py-2.5">
          <img src="./logo.png" alt="" width={16} height={16} className="rounded object-contain" />
          <span className="h-[3px] w-[3px] rounded-full bg-accent" />
          <span className="h-[3px] w-[3px] rounded-full bg-surface-4" />
          <span className="h-[3px] w-[3px] rounded-full bg-surface-4" />
        </div>

        {/* sidebar */}
        <div className="flex w-[86px] flex-none flex-col gap-2 border-r border-line-soft bg-surface-side p-2">
          <div className="flex flex-col items-center gap-1 rounded-[var(--radius)] border border-line-soft bg-surface-1 p-1.5">
            {me ? <Avatar user={me} size={24} presence /> : <span className="h-6 w-6 rounded-[30%] bg-surface-3" />}
            <span className="w-full truncate text-center text-[8px] font-bold">{me?.displayName ?? 'You'}</span>
          </div>
          <span className="h-1.5 w-full rounded-full bg-accent-soft" />
          <span className="h-1.5 w-3/4 rounded-full bg-surface-3" />
          <span className="h-1.5 w-2/3 rounded-full bg-surface-3" />
        </div>

        {/* content */}
        <div className="flex min-w-0 flex-1 flex-col gap-1.5 p-2">
          <div className="flex gap-1.5">
            <span className="h-[14px] flex-1 rounded-[calc(6px*var(--radius-mul,1))] border border-line-soft bg-surface-inset" />
            <span className="h-[14px] w-[34px] rounded-[calc(6px*var(--radius-mul,1))] bg-accent" />
          </div>
          <div className="overflow-hidden rounded-[calc(var(--radius-l)*.8)] border border-accent bg-surface-1 shadow-[0_0_0_1px_var(--accent),0_0_14px_var(--glow)]">
            <span className="block h-[44px] w-full bg-[linear-gradient(120deg,color-mix(in_srgb,var(--accent)_45%,var(--art-base)),var(--art-base))]" />
            <div className="flex flex-col gap-1 p-1.5">
              <span className="h-[6px] w-2/3 rounded-full bg-surface-4" />
              <span className="h-[5px] w-1/2 rounded-full bg-surface-3" />
              <span className="mt-0.5 h-[13px] w-full rounded-[calc(6px*var(--radius-mul,1))] bg-accent" />
            </div>
          </div>
          <div className="overflow-hidden rounded-[calc(var(--radius-l)*.8)] border border-line-soft bg-surface-1">
            <span className="block h-[26px] w-full bg-surface-3" />
          </div>
        </div>
      </div>
    </div>
  );
}


/** One slider row, with the value spelled out in words where that helps. */
function Slider({ label, note, min, max, step, value, display, onChange, last }: {
  label: string;
  note: string;
  min: number;
  max: number;
  step: number;
  value: number;
  display: string;
  onChange: (next: number) => void;
  last?: boolean;
}) {
  /*
   * Label above, control below.
   *
   * Side by side only works when the pane is wide, and this one shares its
   * width with a sub-navigation and a preview - so the label was wrapping
   * into a tall thin column beside a squashed slider.
   */
  return (
    <div className={cn('py-3.5', !last && 'border-b border-line-soft')}>
      <div className="text-[13.5px] font-semibold">{label}</div>
      <div className="text-[12px] text-mute">{note}</div>
      <div className="mt-2 flex items-center gap-3">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="min-w-0 flex-1 accent-[var(--accent)]"
          aria-label={label}
        />
        <span className="w-[84px] flex-none text-right text-[12px] text-mute">{display}</span>
      </div>
    </div>
  );
}
function Segmented<T extends string>({
  value, onChange, options
}: { value: T; onChange: (v: T) => void; options: [T, string][] }) {
  return (
    <div className="inline-flex w-fit max-w-full flex-wrap gap-0.5 self-start rounded-[var(--radius-s)] border border-line bg-surface-2 p-[3px]">
      {options.map(([id, label]) => (
        <button
          key={id}
          type="button"
          onClick={() => onChange(id)}
          className={cn(
            'h-[27px] rounded-[calc(6px*var(--radius-mul,1))] px-3 text-[12px] font-semibold transition-colors',
            value === id ? 'bg-surface-4 text-ink shadow-[var(--shadow-1)]' : 'text-mute hover:text-ink'
          )}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------- voice */

function VoiceTab({ pane = 'devices' }: { pane?: string }) {
  const { settings, saveSettings } = useSession();
  const voice = useVoice();
  const [devices, setDevices] = useState<{ inputs: MediaDeviceInfo[]; outputs: MediaDeviceInfo[] }>({ inputs: [], outputs: [] });
  const [testing, setTesting] = useState(false);

  /*
   * The device list, and it is re-read when hardware appears or disappears.
   *
   * Labels are blank until microphone permission has been granted once, so a
   * device with no label is shown by its position in the list rather than as
   * an empty row.
   */
  useEffect(() => {
    let live = true;
    const load = () => { void listDevices().then((d) => { if (live) setDevices(d); }); };
    load();
    navigator.mediaDevices?.addEventListener?.('devicechange', load);
    return () => {
      live = false;
      navigator.mediaDevices?.removeEventListener?.('devicechange', load);
    };
  }, []);

  if (!settings) return null;

  const inputOptions = [
    { value: '', label: 'System default' },
    ...devices.inputs.map((d, i) => ({ value: d.deviceId, label: d.label || 'Microphone ' + (i + 1) }))
  ];
  const outputOptions = [
    { value: '', label: 'System default' },
    ...devices.outputs.map((d, i) => ({ value: d.deviceId, label: d.label || 'Speakers ' + (i + 1) }))
  ];

  /*
   * Three sub-sections, chosen in the sidebar.
   *
   * This tab was eleven cards in one column: which microphone, how loud, what
   * to suppress, where the call window sits, what the keybind is. Nobody
   * reads eleven cards - they scroll past the one they came for. Split by
   * what you are actually there to do: pick hardware, tune what it sounds
   * like, or decide where the call lives on screen.
   */
  return (
    <>
      {pane === 'devices' ? (
      <>
      <Card>
        <CardHead title="Devices" />
        <div className="grid gap-[var(--gap)] sm:grid-cols-2">
          <Field label="Microphone" hint={voice.partyId ? 'Switches without dropping the call.' : undefined}>
            <Select lead="mic" value={voice.inputId} onChange={(v) => void voice.setInput(v)} options={inputOptions} />
          </Field>
          <Field
            label="Output"
            hint={devices.outputs.length ? undefined : 'Your browser does not let apps choose an output.'}
          >
            <Select
              lead="headset"
              value={voice.outputId}
              onChange={(v) => void voice.setOutput(v)}
              options={outputOptions}
            />
          </Field>
        </div>
        {/* Proof, rather than a list of device names and hope. */}
        <div className="mt-[var(--gap)]">
          <Button kind="outline" icon={<Icon name="mic" size={16} />} onClick={() => setTesting(true)}>
            Test my microphone
          </Button>
        </div>
        {testing ? <MicCheck title="Microphone test" onClose={() => setTesting(false)} /> : null}
      </Card>
      <PushToTalk />
      <Card>
        <CardHead title="In a call" />
        <Toggle checked={settings.autoJoinVoice} onChange={(v) => void saveSettings({ autoJoinVoice: v })}
          label="Join voice automatically" note="When you open a party you are already in" />
        <Toggle checked={settings.confirmLeaveVoice} onChange={(v) => void saveSettings({ confirmLeaveVoice: v })}
          label="Ask before leaving a call" />
        <div className="my-[var(--gap)] h-px bg-line-soft" />
        <Field label="Screen share quality" hint="Frame rate against detail.">
          <Segmented
            value={settings.screenQuality}
            onChange={(v) => void saveSettings({ screenQuality: v })}
            options={[['smooth', 'Smooth 60fps'], ['balanced', 'Balanced 30fps'], ['sharp', 'Sharp 15fps']]}
          />
        </Field>
      </Card>
      </>
      ) : null}

      {pane === 'processing' ? <VoiceProcessing /> : null}
      {pane === 'dock' ? <VoiceDockSettings /> : null}
    </>
  );
}

/* ----------------------------------------------------------- privacy */

function PrivacyTab() {
  const { settings, saveSettings, me } = useSession();
  if (!settings || !me) return null;
  const set = (patch: Record<string, unknown>) => void saveSettings(patch);

  return (
    <>
    <MyReports />
      <SBlock title="Who can reach you" note="Friend requests, messages and invites. What others can see about you is under Activity privacy.">
        <SRow label="Who can send friend requests">
          <Select
            search={false}
            className="min-w-[160px]"
            value={settings.allowFriendRequests ?? 'everyone'}
            onChange={(v) => set({ allowFriendRequests: v })}
            options={[
              { value: 'everyone', label: 'Everyone' },
              { value: 'mutuals', label: 'Friends of friends' },
              { value: 'nobody', label: 'Nobody' }
            ]}
          />
        </SRow>
        <SRow label="Who can message me" note="Only applies to new conversations">
          <Select
            search={false}
            className="min-w-[160px]"
            value={settings.allowDms ?? 'everyone'}
            onChange={(v) => set({ allowDms: v })}
            options={[
              { value: 'everyone', label: 'Everyone' },
              { value: 'friends', label: 'Friends only' },
              { value: 'nobody', label: 'Nobody' }
            ]}
          />
        </SRow>
        <SRow label="Who can invite me to a party">
          <Select
            search={false}
            className="min-w-[160px]"
            value={settings.allowInvites ?? 'friends'}
            onChange={(v) => set({ allowInvites: v })}
            options={[
              { value: 'everyone', label: 'Everyone' },
              { value: 'friends', label: 'Friends only' },
              { value: 'nobody', label: 'Nobody' }
            ]}
          />
        </SRow>
      </SBlock>
    </>
  );
}

function ConnectionTab() {
  const { settings, saveSettings } = useSession();
  const connection = useSession((s) => s.connection);
  const ping = useSession((s) => s.ping);
  const me = useSession((s) => s.me);
  const [address, setAddress] = useState(serverUrl());

  /*
   * Only an admin sees or changes where the app points.
   *
   * For everybody else the address is not a setting - it is how Pugo reaches
   * Pugo, and a field inviting them to change it is a way to break their own
   * install. It also put the server address on a screen it had no reason to
   * be on.
   */
  const isAdmin = me?.role === 'admin';

  return (
    <>
    <UpdateCard />
    <ReportProblem />
    <PrefsBackup />

    {isDesktop() && settings ? (
      <Card>
        <CardHead title="Desktop app" />
        <Toggle
          checked={settings.closeToTray}
          onChange={(v) => { void saveSettings({ closeToTray: v }); void bridge()?.setCloseToTray(v); }}
          label="Keep running when I close the window"
          note="Pugo hides in the tray, next to the hidden icons, so you stay in voice"
        />
        <StartupToggle />
        <div className="mt-[var(--gap)] flex items-center gap-3">
          <span className="text-[12.5px] text-mute">Quit fully from the tray icon, or here.</span>
          <div className="flex-1" />
          <Button kind="outline" size="sm" onClick={() => void bridge()?.quit()}>Quit Pugo</Button>
        </div>
      </Card>
    ) : null}

    <ApiKeysCard />

    <Card>
      <CardHead title="Connection" />
      <div className="mb-[var(--gap)] flex flex-wrap items-center gap-3 text-[13px]">
        <span className="text-mute">Status</span>
        <Tag kind={connection === 'open' ? 'good' : connection === 'closed' ? 'bad' : 'warn'}>
          {connection.toUpperCase()}
        </Tag>
        {ping !== null ? <span className="text-mute">{ping} ms round trip</span> : null}
      </div>
      {isAdmin ? (
        <>
          <Field label="Server address" hint={'Default is ' + DEFAULT_SERVER}>
            <Input value={address} onChange={(e) => setAddress(e.target.value)} spellCheck={false} />
          </Field>
          <div className="mt-[var(--gap)] flex flex-wrap gap-2">
            <Button kind="primary" onClick={() => { setServerUrl(address); location.reload(); }}>Save and reload</Button>
            <Button kind="outline" onClick={() => { setServerUrl(null); location.reload(); }}>Reset to default</Button>
          </div>
        </>
      ) : (
        <p className="text-[12.5px] text-mute">
          Pugo handles the connection for you. If something looks wrong here, sign out and back in.
        </p>
      )}
    </Card>
    </>
  );
}

/* ----------------------------------------------------------- account */

/* One settings card: an icon, what it is, and the control that changes it. */
const SCARD = 'pugo-panel rounded-[calc(18px*var(--radius-mul,1))] bg-surface-1 p-4 shadow-[inset_0_0_0_1px_var(--line-soft)] transition-shadow duration-[250ms] hover:shadow-[inset_0_0_0_1px_var(--line)]';

function SettingRow({ icon, title, note, children, below }: {
  icon: string;
  title: string;
  note?: string;
  children?: ReactNode;
  below?: ReactNode;
}) {
  return (
    <section className={SCARD}>
      <div className="flex flex-wrap items-center gap-4">
        <Icon name={icon} size={26} className="flex-none text-ink" />
        <div className="min-w-0 flex-1 basis-[220px]">
          <h3 className="m-0 text-[16.5px] font-bold text-ink">{title}</h3>
          {note ? <p className="mt-0.5 mb-0 text-[14px] leading-snug text-mute">{note}</p> : null}
          {below}
        </div>
        {children ? <div className="flex flex-none items-center gap-2">{children}</div> : null}
      </div>
    </section>
  );
}

/** A quiet button that reveals the real control under a card. */
function Reveal({ label, open, onClick }: { label: string; open: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-expanded={open}
      onClick={onClick}
      className="flex h-11 items-center gap-2 rounded-[calc(12px*var(--radius-mul,1))] bg-surface-1 px-4 text-[14.5px] font-semibold text-ink shadow-[inset_0_0_0_1px_var(--line)] outline-none transition-[background-color,box-shadow] duration-[250ms] hover:bg-surface-2 focus-visible:shadow-[0_0_0_2px_var(--accent)]"
    >
      {label}
      <Icon name="chevronD" size={15} className={cn('text-mute transition-transform duration-[250ms]', open && 'rotate-180')} />
    </button>
  );
}

/**
 * An address you can recognise without reading it out.
 *
 * Keeps the first and last letter of the name and the domain, which is
 * enough to know which of your addresses it is and not enough for anybody
 * watching to write it down.
 */
function maskEmail(email: string): string {
  const at = email.lastIndexOf('@');
  if (at < 1) return '•'.repeat(Math.max(3, email.length));
  const name = email.slice(0, at);
  const host = email.slice(at + 1);
  const keep = (t: string) => (t.length <= 2 ? t[0] + '•' : t[0] + '•'.repeat(Math.max(1, t.length - 2)) + t[t.length - 1]);
  const dot = host.lastIndexOf('.');
  const hostMasked = dot > 0 ? keep(host.slice(0, dot)) + host.slice(dot) : keep(host);
  return keep(name) + '@' + hostMasked;
}

function AccountTab() {
  const { me, settings, signOut } = useSession();
  const { confirm, dialog } = useConfirm();
  const [open, setOpen] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  /* Not remembered anywhere: leaving the page covers it again. */
  const [showEmail, setShowEmail] = useState(false);
  const twoFactor = useQuery({
    queryKey: ['auth', '2fa'],
    queryFn: () => api.get<{ enabled: boolean; method: 'app' | 'email' | null; recoveryLeft: number }>('/api/auth/2fa')
  });
  const sessions = useQuery({
    queryKey: ['me', 'sessions'],
    queryFn: () => api.get<{ sessions: { id: string; agent: string; ip: string; createdAt: number; lastUsed: number; current: boolean }[] }>('/api/me/sessions')
  });
  if (!me || !settings) return null;

  const toggle = (id: string) => setOpen((now) => (now === id ? null : id));
  const deviceName = isDesktop() ? 'Desktop app' : 'Web browser';

  return (
    <>
      <PageHead title="Your account" note="Manage your account details, security and active sessions." />

      {/* ---- who you are ---- */}
      <section className={cn(SCARD, 'flex flex-wrap items-center gap-5')}>
        <Avatar user={me} size={104} radius="calc(24px*var(--radius-mul,1))" presence />
        <div className="min-w-0 flex-1 basis-[200px]">
          <h3 className="m-0 text-[26px] font-bold tracking-[-.02em] text-ink"><UserName user={me} profile={false} /></h3>
          <p className="mt-0.5 mb-0 text-[15.5px] text-mute">@{me.username}</p>
          <p className="mt-1 mb-0 truncate text-[14.5px] text-dim">{me.customStatus || me.bio || 'Level ' + me.level}</p>
        </div>
        <Reveal label="Edit profile" open={false} onClick={() => go('settings/profile')} />
      </section>

      {/*
        Where this account stands.

        First on the page because it is the thing somebody opens this page
        to find out - moderation used to be invisible to the person it
        happened to, so the first thing most people learned about their own
        record was the ban.
      */}
      <section className={cn(SCARD, 'mb-[var(--gap)]')}>
        <AccountStanding />
      </section>

      {/* ---- email ---- */}
      <SettingRow
        icon="mail"
        title="Email"
        note="Your email is used to sign in and receive important updates."
        below={
          <p className="mt-2 mb-0 flex flex-wrap items-center gap-2.5 text-[15px] text-ink">
            {/*
              Covered by default.

              Settings gets opened on a shared screen, on a stream, and over
              somebody's shoulder, and the address was simply printed there.
              It is masked until you ask for it, and it covers itself again
              the moment you leave the page.
            */}
            {me.email
              ? <span className="font-mono tracking-tight">{showEmail ? me.email : maskEmail(me.email)}</span>
              : 'No email yet'}
            {me.email ? (
              <button
                type="button"
                onClick={() => setShowEmail((v) => !v)}
                aria-pressed={showEmail}
                className="flex items-center gap-1 rounded-[calc(8px*var(--radius-mul,1))] px-1.5 py-0.5 text-[12.5px] font-semibold text-mute outline-none transition-[background-color,color] duration-[var(--t-hover)] hover:bg-surface-2 hover:text-ink focus-visible:shadow-[0_0_0_2px_var(--accent)]"
              >
                <Icon name={showEmail ? 'eyeOff' : 'eye'} size={13} />
                {showEmail ? 'Hide' : 'Show'}
              </button>
            ) : null}
            {me.email ? (
              <span className={cn('flex items-center gap-1 rounded-full px-2 py-0.5 text-[12.5px] font-semibold', me.emailVerified ? 'bg-[color-mix(in_srgb,var(--good)_14%,transparent)] text-good' : 'bg-[color-mix(in_srgb,var(--warn)_16%,transparent)] text-warn')}>
                <Icon name={me.emailVerified ? 'checkCirc' : 'alert'} size={13} />
                {me.emailVerified ? 'Verified' : 'Not verified'}
              </span>
            ) : null}
          </p>
        }
      >
        <Reveal label="Change" open={open === 'email'} onClick={() => toggle('email')} />
      </SettingRow>
      {open === 'email' ? <div className="animate-[fade-in_250ms_ease-out] motion-reduce:animate-none"><EmailCard /></div> : null}

      {/* ---- password ---- */}
      <SettingRow
        icon="lock"
        title="Password"
        note="Keep your account secure with a strong password."
        below={<p className="mt-2 mb-0 text-[18px] leading-none tracking-[.2em] text-mute">••••••••••</p>}
      >
        <Reveal label="Change password" open={open === 'password'} onClick={() => toggle('password')} />
      </SettingRow>
      {open === 'password' ? <div className="animate-[fade-in_250ms_ease-out] motion-reduce:animate-none"><PasswordCard /></div> : null}

      {/* ---- two-factor ---- */}
      <SettingRow
        icon="shield"
        title="Two-factor authentication"
        note="Add an extra layer of security to your account."
        below={
          twoFactor.data ? (
            <p className="mt-2 mb-0">
              <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[12.5px] font-semibold', twoFactor.data.enabled ? 'bg-[color-mix(in_srgb,var(--good)_14%,transparent)] text-good' : 'bg-surface-3 text-dim')}>
                <Icon name={twoFactor.data.enabled ? 'checkCirc' : 'info'} size={13} />
                {twoFactor.data.enabled ? 'Enabled' : 'Off'}
              </span>
            </p>
          ) : null
        }
      >
        <Reveal label="Manage" open={open === '2fa'} onClick={() => toggle('2fa')} />
      </SettingRow>
      {open === '2fa' ? <div className="animate-[fade-in_250ms_ease-out] motion-reduce:animate-none"><TwoFactorCard /></div> : null}

      {/* ---- devices ---- */}
      <SettingRow
        icon="laptop"
        title="Active sessions"
        note="These are the devices currently signed in to your account."
        below={
          <p className="mt-2 mb-0 text-[15px] text-ink">
            This device · {deviceName}
            {sessions.data ? <span className="text-mute"> · {sessions.data.sessions.length} signed in</span> : null}
          </p>
        }
      >
        <Reveal label="View devices" open={open === 'devices'} onClick={() => toggle('devices')} />
      </SettingRow>
      {open === 'devices' ? (
        <div className={cn(SCARD, 'animate-[fade-in_250ms_ease-out] motion-reduce:animate-none')}>
          {sessions.isPending ? (
            <p className="m-0 text-[14px] text-mute">Looking…</p>
          ) : !sessions.data?.sessions.length ? (
            <p className="m-0 text-[14px] text-mute">No other devices.</p>
          ) : (
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {sessions.data.sessions.map((s) => (
                <li key={s.id} className="flex flex-wrap items-center gap-3 rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2 px-3 py-2.5">
                  <Icon name={/mobile|android|iphone/i.test(s.agent) ? 'profile' : 'laptop'} size={20} className="flex-none text-mute" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[14.5px] font-semibold text-ink">
                      {s.agent ? s.agent.replace(/\s*\(.*?\)\s*/g, ' ').slice(0, 48) : 'Unknown device'}
                      {s.current ? <span className="ml-2 rounded-full bg-accent-soft px-2 py-0.5 text-[12px] font-semibold text-accent">This device</span> : null}
                    </span>
                    <span className="block text-[13px] text-mute">Last used {new Date(s.lastUsed || s.createdAt).toLocaleString()}</span>
                  </span>
                  {!s.current ? (
                    <Button
                      kind="outline"
                      size="sm"
                      onClick={async () => { await endpoints.revokeSession(s.id); void sessions.refetch(); }}
                    >
                      Sign out
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}

      {/* ---- leaving ---- */}
      <div className="mt-2 flex flex-wrap items-center gap-3 border-t border-line-soft pt-5">
        <button
          type="button"
          onClick={async () => {
            if (await confirm('Sign out?', 'You will need your password to get back in.', { confirmLabel: 'Sign out' })) signOut();
          }}
          className="flex h-11 items-center gap-2 rounded-[calc(12px*var(--radius-mul,1))] bg-surface-1 px-4 text-[14.5px] font-semibold text-bad shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--bad)_35%,var(--line))] outline-none transition-colors duration-[250ms] hover:bg-[color-mix(in_srgb,var(--bad)_10%,transparent)] focus-visible:shadow-[0_0_0_2px_var(--accent)]"
        >
          <Icon name="logout" size={17} />
          Sign out
        </button>
        <button
          type="button"
          onClick={async () => {
            const ok = await confirm('Sign out everywhere?', 'Every device signed in to this account is signed out, including this one.', { confirmLabel: 'Sign out everywhere', danger: true });
            if (ok) signOut(true);
          }}
          className="h-11 rounded-[calc(12px*var(--radius-mul,1))] px-3 text-[14.5px] font-semibold text-mute outline-none transition-colors duration-[250ms] hover:text-ink focus-visible:shadow-[0_0_0_2px_var(--accent)]"
        >
          Sign out everywhere
        </button>
        <span className="flex-1" />
        <button
          type="button"
          onClick={() => setDeleting(true)}
          className="flex h-11 items-center gap-2 rounded-[calc(12px*var(--radius-mul,1))] px-3 text-[14.5px] font-semibold text-bad outline-none transition-colors duration-[250ms] hover:bg-[color-mix(in_srgb,var(--bad)_10%,transparent)] focus-visible:shadow-[0_0_0_2px_var(--accent)]"
        >
          <Icon name="trash" size={17} />
          Delete account
        </button>
      </div>

      {deleting ? <DeleteAccount onClose={() => setDeleting(false)} /> : null}
      {dialog}
    </>
  );
}

/** The title over a settings section. */
function PageHead({ title, note }: { title: string; note: string }) {
  return (
    <header className="mb-1">
      <h2 className="m-0 text-[30px] leading-tight font-extrabold tracking-[-.03em] text-ink">{title}</h2>
      <p className="mt-1 mb-0 text-[16px] text-mute">{note}</p>
    </header>
  );
}

/**
 * Deleting the account, which needs the password and cannot be undone.
 *
 * The password is typed here and sent straight to the server; nothing keeps it.
 */
function DeleteAccount({ onClose }: { onClose: () => void }) {
  const [password, setPassword] = useState('');
  const [confirmWord, setConfirmWord] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const toast = useToast();
  const signOut = useSession((s) => s.signOut);
  /* Pressing past it is a cancel, like every other dialog in the app. */
  const backdrop = useBackdropClose(onClose);

  async function go2() {
    if (confirmWord.trim().toUpperCase() !== 'DELETE') { setError('Type DELETE to confirm.'); return; }
    setBusy(true);
    setError('');
    try {
      await api.del('/api/me', { password });
      toast('Your account has been deleted.', 'good');
      signOut();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return createPortal(
    <div
      {...backdrop}
      className="fixed inset-0 z-[400] grid place-items-center bg-[rgba(3,5,10,.7)] p-4 backdrop-blur-[4px]"
      role="dialog"
      aria-modal="true"
      aria-label="Delete account"
    >
      <div className="pugo-panel w-full max-w-[460px] rounded-[calc(22px*var(--radius-mul,1))] bg-surface-1 p-6 shadow-[var(--shadow-3)]">
        <h3 className="m-0 text-[22px] font-bold text-ink">Delete your account</h3>
        <p className="mt-2 mb-4 text-[14.5px] leading-relaxed text-dim">
          This removes your profile, messages, uploads and memberships. It cannot be undone.
        </p>
        <Field label="Your password">
          <Input type="password" autoComplete="current-password" value={password} onChange={(e) => { setPassword(e.target.value); setError(''); }} />
        </Field>
        <div className="mt-3">
          <Field label="Type DELETE to confirm">
            <Input value={confirmWord} onChange={(e) => { setConfirmWord(e.target.value); setError(''); }} spellCheck={false} />
          </Field>
        </div>
        {error ? <p role="alert" className="mt-3 mb-0 text-[14px] text-bad">{error}</p> : null}
        <div className="mt-5 flex justify-end gap-2">
          <Button kind="plain" onClick={onClose}>Cancel</Button>
          <Button kind="danger" disabled={busy || !password} onClick={() => void go2()}>{busy ? 'Deleting…' : 'Delete account'}</Button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function UpdateCard() {
  const [state, setState] = useState<UpdateState | null>(null);
  const api = bridge();

  useEffect(() => {
    if (!api?.update) return;
    void api.update.state().then(setState);
    return api.update.onState(setState);
  }, [api]);

  if (!api?.update) {
    return (
      <Card>
        <CardHead title="Updates" />
        <p className="text-[13px] text-mute">
          You are on the web version — it is always the latest.
        </p>
      </Card>
    );
  }

  const status = state?.status ?? 'idle';
  const line =
    status === 'checking' ? 'Checking…'
      : status === 'downloading' ? `Downloading ${state?.latest} — ${Math.round((state?.progress ?? 0) * 100)}%`
        : status === 'available' ? `Version ${state?.latest} is available. You have ${state?.current}.`
          : status === 'ready' ? `Version ${state?.latest} is ready to install.`
            : status === 'error' ? (state?.error ?? 'The update check failed.')
              : status === 'current' ? `You are on the latest version (${state?.current}).`
                : `Version ${state?.current ?? ''}`;

  return (
    <Card>
      <CardHead title="Updates" note="Installs in place — no reinstall" />
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-[13px] text-dim">{line}</span>
        <div className="flex-1" />
        <Button
          kind="primary"
          disabled={status === 'checking' || status === 'downloading'}
          onClick={() => {
            if (status === 'available') void api.update.download();
            else if (status === 'ready') void api.update.install();
            else void api.update.check(serverUrl());
          }}
        >
          {status === 'available' ? 'Download update'
            : status === 'ready' ? 'Restart and update'
              : status === 'downloading' ? 'Downloading'
                : 'Check for updates'}
        </Button>
      </div>
      {status === 'downloading' ? (
        <div className="mt-3 h-1.5 overflow-hidden rounded bg-surface-3">
          <i className="block h-full rounded bg-gradient-to-r from-accent-lo to-accent-hi transition-[width]"
            style={{ width: `${Math.round((state?.progress ?? 0) * 100)}%` }} />
        </div>
      ) : null}
      {state?.notes?.length ? (
        <ul className="mt-3 list-disc pl-5 text-[12.5px] text-mute">
          {state.notes.map((n) => <li key={n} className="my-0.5">{n}</li>)}
        </ul>
      ) : null}
    </Card>
  );
}

/**
 * Changing your password.
 *
 * The server issues a fresh token on success and invalidates the rest, so the
 * new one is stored immediately - otherwise changing your password would sign
 * you out of the tab you did it in.
 */
function PasswordCard() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const mismatch = again.length > 0 && next !== again;
  const ready = current.length > 0 && next.length > 0 && next === again && !busy;

  async function change() {
    setBusy(true);
    try {
      const res = await api.post<{ token: string }>('/api/auth/password', { current, next });
      setToken(res.token);
      setCurrent(''); setNext(''); setAgain('');
      toast('Password changed. Other devices were signed out.', 'good');
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHead title="Password" note="Changing it signs out every other device" />
      <div className="grid gap-[var(--gap)] sm:grid-cols-3">
        <Field label="Current password">
          <Input
            type="password"
            value={current}
            autoComplete="current-password"
            onChange={(e) => setCurrent(e.target.value)}
          />
        </Field>
        <Field label="New password">
          <Input
            type="password"
            value={next}
            autoComplete="new-password"
            onChange={(e) => setNext(e.target.value)}
          />
        </Field>
        <Field label="Repeat it" hint={mismatch ? 'These do not match.' : undefined}>
          <Input
            type="password"
            value={again}
            autoComplete="new-password"
            onChange={(e) => setAgain(e.target.value)}
          />
        </Field>
      </div>
      <div className="mt-[var(--gap)] flex">
        <div className="flex-1" />
        <Button kind="primary" disabled={!ready} onClick={() => void change()}>
          {busy ? 'Changing…' : 'Change password'}
        </Button>
      </div>
    </Card>
  );
}

/**
 * A grid of cosmetics you can wear, with each one drawn as itself.
 *
 * Shared by the profile-page frames and the banner effects: both are a list
 * from the catalogue, both are gated by membership, and both are impossible to
 * choose from by name - "Nebula" tells you nothing until you have seen it.
 *
 * Locked ones are shown rather than hidden. Knowing a thing exists is most of
 * the point of it being a perk.
 */
/** Whether somebody on this membership may wear something needing that one. */
function tierAllows(has: string, need: string): boolean {
  if (need === 'free') return true;
  if (need === 'premium') return has === 'premium' || has === 'vip';
  return has === 'vip';
}


/**
 * The games you play, on their own screen.
 *
 * It was the tail of the profile page, under your name and your languages,
 * and it is the longest thing there by far - a row per game with a rank and
 * roles on each. Its own tab means neither list has to be scrolled past to
 * reach the other.
 */
function GamesTab() {
  return (
    <Card>
      <CardHead
        title="Your games"
        note="What you play, your rank in each, and the roles you take"
      />
      <EditProfile inline section="games" onClose={() => {}} />
    </Card>
  );
}

/**
 * What your membership actually gets you.
 *
 * The perks were spread across four screens - a locked style here, a greyed
 * toggle there - so there was no way to find out what Premium was for without
 * hunting. This is the one page that answers it, and it says plainly which
 * things you already have.
 *
 * Nothing here sells anything. Tiers are granted by an admin, not bought in
 * the client, so the page states the facts and stops.
 */
const PERKS: { label: string; note: string; free: string; premium: string; vip: string }[] = [
  {
    label: 'Party size',
    note: 'How many people you can put in one party',
    free: '5', premium: '20', vip: '50'
  },
  {
    label: 'Party card styles',
    note: 'How your party looks in the browse list',
    free: '3', premium: '7', vip: 'All 10'
  },
  {
    label: 'Profile page frames',
    note: 'A border around your whole profile',
    free: '7', premium: '10', vip: 'All 11'
  },
  {
    label: 'Banner effects',
    note: 'What the banner behind your name does',
    free: '4', premium: '7', vip: 'All 8'
  },
  {
    label: 'Screen share quality',
    note: 'The highest you can push a share',
    free: '1080p', premium: '1440p', vip: '1440p'
  },
  {
    label: 'Animated picture',
    note: 'A moving avatar, rather than a still one',
    free: '—', premium: 'Yes', vip: 'Yes'
  },
  {
    label: 'Animated banner',
    note: 'A moving picture behind your name',
    free: '—', premium: 'Yes', vip: 'Yes'
  },
  {
    label: 'Name colour',
    note: 'Your own colour, and a gradient that drifts',
    free: '—', premium: 'Yes', vip: 'Yes'
  },
  {
    label: 'Profile accent',
    note: 'Tints your whole profile page',
    free: '—', premium: 'Yes', vip: 'Yes'
  }
];

function PremiumTab() {
  const me = useSession((s) => s.me);
  if (!me) return null;

  const tier = me.tier;
  const admin = me.role === 'admin';
  const columns: { id: 'free' | 'premium' | 'vip'; label: string; mine: boolean; tone: string }[] = [
    { id: 'free', label: 'Standard', mine: tier === 'none', tone: 'text-dim' },
    { id: 'premium', label: 'Premium', mine: tier === 'premium', tone: 'text-epic' },
    { id: 'vip', label: 'VIP', mine: tier === 'vip', tone: 'text-mythic' }
  ];

  return (
    <>
      <Card>
        <CardHead
          title="Your membership"
          note="Granted by staff rather than bought here, so nothing on this page asks for money"
        />
        <div className="flex flex-wrap items-center gap-3 rounded-[var(--radius)] border border-line-soft bg-surface-1 p-3">
          <Avatar user={me} size={44} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <UserName user={me} className="text-[15px] font-bold" />
              <span
                className={cn(
                  'rounded px-1.5 text-[10px] font-extrabold tracking-[.06em]',
                  tier === 'vip'
                    ? 'bg-[color-mix(in_srgb,var(--tier-mythic)_22%,transparent)] text-mythic'
                    : tier === 'premium'
                      ? 'bg-[color-mix(in_srgb,var(--tier-epic)_22%,transparent)] text-epic'
                      : 'bg-surface-3 text-mute'
                )}
              >
                {tier === 'vip' ? 'VIP' : tier === 'premium' ? 'PREMIUM' : 'STANDARD'}
              </span>
            </div>
            <p className="text-[12.5px] text-mute">
              {admin
                ? 'You are an admin, so party size has no ceiling for you whatever your membership says.'
                : tier === 'none'
                  ? 'Everything below in the Standard column is yours today.'
                  : 'Everything up to and including your column is yours.'}
            </p>
          </div>
        </div>
      </Card>

      <Card>
        <CardHead title="What each one gets" note="Your column is highlighted" />
        <div className="overflow-x-auto">
          <table className="w-full min-w-[440px] border-collapse text-left">
            <thead>
              <tr>
                <th className="pb-2 text-[10.5px] font-bold tracking-[.07em] text-mute uppercase">Perk</th>
                {columns.map((c) => (
                  <th
                    key={c.id}
                    className={cn(
                      'pb-2 text-center text-[10.5px] font-bold tracking-[.07em] uppercase',
                      c.mine ? c.tone : 'text-ghost'
                    )}
                  >
                    {c.label}
                    {c.mine ? <span className="ml-1 text-accent-hi">•</span> : null}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PERKS.map((p) => (
                <tr key={p.label} className="border-t border-line-soft">
                  <td className="py-2.5 pr-3">
                    <span className="block text-[13px] font-semibold">{p.label}</span>
                    <span className="block text-[11.5px] text-mute">{p.note}</span>
                  </td>
                  {columns.map((c) => (
                    <td
                      key={c.id}
                      className={cn(
                        'px-2 py-2.5 text-center text-[13px] font-bold tabular-nums',
                        c.mine ? 'text-ink' : 'text-mute',
                        c.mine && 'bg-[color-mix(in_srgb,var(--accent)_8%,transparent)]'
                      )}
                    >
                      {p[c.id]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}

/* ------------------------------------------------------- settings bits */

/**
 * One setting on its own line.
 *
 * The reference lays settings out as rows in a plain vertical list rather
 * than as a grid of cards - a card per toggle turns nine switches into nine
 * boxes to read past.
 */
/*
 * The row's visible label, handed to the control inside it.
 *
 * Switches announced themselves as "switch, on" with no name: the label sat in
 * a div next to the button and nothing connected the two. A screen reader user
 * heard a column of anonymous switches. The row now publishes the ids of its
 * label and note, and the control points at them.
 */
const RowLabel = createContext<{ labelId: string; noteId?: string } | null>(null);

function SRow({ label, note, children }: { label: string; note?: string; children?: ReactNode }) {
  const id = useId();
  const labelId = id + 'label';
  const noteId = note ? id + 'note' : undefined;
  return (
    // srow is the hook the phone stylesheet uses to stack these and give the
    // control a whole line to itself.
    <div className="srow flex items-center gap-4 border-b border-line-soft py-3.5 last:border-b-0">
      <div className="min-w-0 flex-1">
        <div id={labelId} className="text-[15px] font-semibold text-ink">{label}</div>
        {note ? <div id={noteId} className="mt-0.5 text-[13.5px] text-mute">{note}</div> : null}
      </div>
      {children ? (
        <RowLabel.Provider value={{ labelId, noteId }}>
          <div className="flex flex-none items-center gap-2">{children}</div>
        </RowLabel.Provider>
      ) : null}
    </div>
  );
}

/** A titled block of rows. */
function SBlock({ title, note, children }: { title: string; note?: string; children: ReactNode }) {
  return (
    <section className="pugo-panel rounded-[calc(18px*var(--radius-mul,1))] bg-surface-1 p-4 shadow-[inset_0_0_0_1px_var(--line-soft)] transition-shadow duration-[250ms] hover:shadow-[inset_0_0_0_1px_var(--line)]">
      <h3 className="m-0 text-[16.5px] font-bold text-ink">{title}</h3>
      {note ? <p className="mt-0.5 mb-1 text-[14px] text-mute">{note}</p> : null}
      <div className="mt-2">{children}</div>
    </section>
  );
}

/** The switch used on every settings row. */
function SwitchC({ on, onChange, disabled, label }: {
  on: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  /** Only needed outside an SRow, where there is no visible label to point at. */
  label?: string;
}) {
  const row = useContext(RowLabel);
  return (
    <Switch
      checked={on}
      onChange={onChange}
      disabled={disabled}
      label={label}
      labelledBy={label ? undefined : row?.labelId}
      describedBy={row?.noteId}
    />
  );
}

/* --------------------------------------------------- connected accounts */

/**
 * The services you can link.
 *
 * Nothing here is wired to a real OAuth flow yet, so every provider reports
 * itself as not connected and Connect explains what linking would do rather
 * than pretending it happened. Showing a green "Connected" we cannot back up
 * would be a lie the profile then repeats.
 */
/* ------------------------------------------------------- notifications */

/** The kinds of thing that can interrupt you, each silenced on its own. */
const NOTIFY_KINDS = [
  { id: 'dm', icon: 'chat', label: 'Direct messages', note: 'Get notified when someone sends you a message.' },
  { id: 'mention', icon: 'at', label: 'Mentions', note: 'Get notified when someone mentions you.' },
  { id: 'invite', icon: 'friends', label: 'Party invites', note: "Get notified when you're invited to a party." },
  { id: 'friend', icon: 'addFriend', label: 'Friend requests', note: 'Get notified when someone sends you a friend request.' },
  { id: 'party', icon: 'gamepad', label: 'Party activity', note: 'Get notified about activity in your parties.' },
  { id: 'call', icon: 'headset', label: 'Calls', note: 'Get notified when someone starts talking in your party.' }
];

function NotificationsTab() {
  const { settings, saveSettings, me } = useSession();
  const [permission, setPermission] = useState<NotificationPermission | 'app'>(() =>
    bridge()?.notify ? 'app' : typeof Notification === 'undefined' ? 'denied' : Notification.permission);
  const [quietOpen, setQuietOpen] = useState(false);
  const s = settings;
  if (!s) return null;
  const set = (patch: Record<string, unknown>) => void saveSettings(patch);
  const quiet = s.quiet ?? { on: false, from: '22:00', to: '08:00' };
  const allowed = permission === 'app' || permission === 'granted';

  return (
    <>
      <PageHead title="Notifications" note="Choose what gets your attention." />

      <div className="grid gap-5 @4xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="flex min-w-0 flex-col gap-4">
          {/*
            One choice that writes the rest.

            Nobody thinks in eleven switches. They think "everything",
            "only the things aimed at me" or "leave me alone" - so those are
            the three buttons, and each one sets the switches below to match.
            Changing a switch afterwards is allowed; the preset is a starting
            point, not a mode.
          */}
          <section className={SCARD}>
            <h3 className="m-0 text-[16.5px] font-bold text-ink">How much should reach you?</h3>
            <p className="mt-0.5 mb-3 text-[14px] text-mute">A starting point. Everything below can still be changed on its own.</p>
            <div role="radiogroup" aria-label="Notification preset" className="grid gap-2 @xl:grid-cols-3">
              {([
                ['all', 'Everything', 'Every message, invite and mention'],
                ['mentions', 'Mentions and invites', 'Direct messages, mentions, invites and calls'],
                ['quiet', 'Quiet', 'Only an incoming call breaks through']
              ] as const).map(([id, label, note]) => {
                const on = (s.notifyPreset ?? 'all') === id;
                return (
                  <button
                    key={id}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    onClick={() => set({ notifyPreset: id })}
                    className={cn(
                      'rounded-[calc(14px*var(--radius-mul,1))] p-3 text-left outline-none',
                      'transition-[background-color,box-shadow] duration-[200ms] focus-visible:shadow-[0_0_0_2px_var(--accent)]',
                      on ? 'bg-accent text-accent-fg' : 'bg-surface-2 text-ink shadow-[inset_0_0_0_1px_var(--line-soft)] hover:bg-surface-3'
                    )}
                  >
                    <span className="block text-[15px] font-bold">{label}</span>
                    <span className={cn('mt-0.5 block text-[12.5px]', on ? 'opacity-85' : 'text-mute')}>{note}</span>
                  </button>
                );
              })}
            </div>
          </section>

          {/* ---- desktop ---- */}
          <SettingRow
            icon="monitor"
            title="Desktop notifications"
            note="Show notifications on this device."
            below={
              <p className={cn('mt-2 mb-0 flex items-start gap-2 text-[14px]', allowed ? 'text-good' : 'text-warn')}>
                <Icon name={allowed ? 'checkCirc' : 'alert'} size={16} className="mt-px flex-none" />
                <span>
                  <span className="block font-semibold">
                    {permission === 'app' ? 'Notifications are allowed'
                      : permission === 'granted' ? 'Notifications are allowed'
                        : permission === 'denied' ? 'Blocked by Windows or your browser'
                          : 'Not allowed yet'}
                  </span>
                  <span className="block text-mute">
                    {allowed ? "You'll see notifications on your desktop."
                      : permission === 'denied' ? 'Allow Pugo to send notifications in your system settings.'
                        : 'Allow them so invites and messages can reach you.'}
                  </span>
                </span>
              </p>
            }
          >
            {permission === 'default' ? (
              <Button kind="outline" onClick={() => void Notification.requestPermission().then(setPermission)}>Allow</Button>
            ) : null}
            <SwitchC on={s.notifications !== false} onChange={(v) => set({ notifications: v })} label="Desktop notifications" />
          </SettingRow>

          {/* ---- sounds ---- */}
          <SettingRow
            icon="volume"
            title="Sounds"
            note="Play a sound when you receive a notification."
            below={
              <div className="mt-3 max-w-[420px]">
                <p className="m-0 mb-1.5 text-[14px] font-medium text-ink">Volume</p>
                <div className="flex items-center gap-3">
                  <Icon name="volumeOff" size={17} className="flex-none text-mute" />
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.05}
                    disabled={s.sounds === false}
                    value={s.soundVolume ?? 0.5}
                    aria-label="Notification volume"
                    onChange={(e) => set({ soundVolume: Number(e.target.value) })}
                    className="h-2 flex-1 cursor-pointer appearance-none rounded-full bg-surface-4 accent-[var(--accent)] disabled:opacity-50"
                    style={{ background: 'linear-gradient(to right, var(--accent) ' + Math.round((s.soundVolume ?? 0.5) * 100) + '%, var(--bg-4) ' + Math.round((s.soundVolume ?? 0.5) * 100) + '%)' }}
                  />
                  <Icon name="volume" size={19} className="flex-none text-mute" />
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {SOUND_NAMES.map((name) => (
                    <button
                      key={name}
                      type="button"
                      disabled={s.sounds === false}
                      onClick={() => preview(name)}
                      className="rounded-full bg-surface-2 px-3 py-1 text-[12.5px] font-semibold text-dim shadow-[inset_0_0_0_1px_var(--line-soft)] transition-colors hover:text-ink disabled:opacity-50"
                    >
                      {name.replace(/([A-Z])/g, ' $1').toLowerCase()}
                      {isSampled(name) ? <span className="ml-1 text-accent" title="A recorded sound">*</span> : null}
                    </button>
                  ))}
                </div>
              </div>
            }
          >
            <SwitchC on={s.sounds !== false} onChange={(v) => set({ sounds: v })} label="Notification sounds" />
          </SettingRow>

          {/* ---- kinds ---- */}
          <section className={SCARD}>
            <h3 className="m-0 text-[16.5px] font-bold text-ink">Notification types</h3>
            <p className="mt-0.5 mb-3 text-[14px] text-mute">Choose which events trigger notifications.</p>
            <ul className="m-0 flex list-none flex-col p-0">
              {NOTIFY_KINDS.map((k) => (
                <li key={k.id} className="flex items-center gap-4 border-b border-line-soft py-3 last:border-b-0">
                  <Icon name={k.icon} size={22} className="flex-none text-ink" />
                  <div className="min-w-0 flex-1">
                    <p className="m-0 text-[15px] font-semibold text-ink">{k.label}</p>
                    <p className="mt-0.5 mb-0 text-[13.5px] text-mute">{k.note}</p>
                  </div>
                  <SwitchC
                    label={k.label}
                    on={s.notify?.[k.id] !== false}
                    disabled={s.notifications === false}
                    onChange={(v) => set({ notify: { ...(s.notify ?? {}), [k.id]: v } })}
                  />
                </li>
              ))}
            </ul>
            {s.notifications === false ? (
              <p className="mt-3 mb-0 text-[13.5px] text-warn">Desktop notifications are off, so none of these will show.</p>
            ) : null}
          </section>

          {/* ---- quiet time ---- */}
          <SettingRow
            icon="moon"
            title="Quiet time"
            note="Pause all notifications during a set schedule."
            below={
              <>
                <p className="mt-1 mb-0 text-[14px] font-medium text-ink">Do not disturb</p>
                {quietOpen ? (
                  <div className="mt-3 flex flex-wrap items-center gap-3 animate-[fade-in_250ms_ease-out] motion-reduce:animate-none">
                    <label className="flex items-center gap-2 text-[14px] text-dim">
                      From
                      <input
                        type="time"
                        value={quiet.from}
                        onChange={(e) => set({ quiet: { ...quiet, from: e.target.value } })}
                        className="h-10 rounded-[10px] bg-surface-2 px-2.5 text-[14px] text-ink shadow-[inset_0_0_0_1px_var(--line-soft)] outline-none focus:shadow-[inset_0_0_0_1px_var(--accent)]"
                      />
                    </label>
                    <label className="flex items-center gap-2 text-[14px] text-dim">
                      to
                      <input
                        type="time"
                        value={quiet.to}
                        onChange={(e) => set({ quiet: { ...quiet, to: e.target.value } })}
                        className="h-10 rounded-[10px] bg-surface-2 px-2.5 text-[14px] text-ink shadow-[inset_0_0_0_1px_var(--line-soft)] outline-none focus:shadow-[inset_0_0_0_1px_var(--accent)]"
                      />
                    </label>
                    <SwitchC on={quiet.on} onChange={(v) => set({ quiet: { ...quiet, on: v } })} label="Quiet time" />
                  </div>
                ) : null}
              </>
            }
          >
            <span className="text-[14.5px] font-medium text-mute">{quiet.on ? quiet.from + ' – ' + quiet.to : 'Off'}</span>
            <Reveal label="Configure" open={quietOpen} onClick={() => setQuietOpen(!quietOpen)} />
          </SettingRow>

          <NotifyDoctor />

          <SBlock title="Friends">
            <SRow label="Let pinned friends through quiet time">
              <SwitchC on={!!s.favoriteExceptions} onChange={(v) => set({ favoriteExceptions: v })} />
            </SRow>
            <SRow label="Tell me when a friend comes online">
              <SwitchC on={!!s.notifyFriendOnline} onChange={(v) => set({ notifyFriendOnline: v })} />
            </SRow>
          </SBlock>
        </div>

        <NotificationPreview me={me} />
      </div>
    </>
  );
}

/**
 * What a notification looks like, drawn from your own account.
 *
 * A picture of the real thing rather than a made-up person: your first game if
 * you have one, and the buttons a party invite actually carries.
 */
function NotificationPreview({ me }: { me: ReturnType<typeof useSession.getState>['me'] }) {
  const catalog = useSession((s) => s.catalog);
  const game = catalog?.games.find((g) => g.id === (me?.games?.[0]?.id ?? ''));

  return (
    <aside aria-label="Preview" className={cn(SCARD, 'h-fit @4xl:sticky @4xl:top-0')}>
      <h3 className="m-0 text-[17px] font-bold text-ink">Preview</h3>
      <p className="mt-0.5 mb-4 text-[14px] text-mute">Here&apos;s how a notification might look.</p>

      <div aria-hidden className="overflow-hidden rounded-[calc(16px*var(--radius-mul,1))] bg-[linear-gradient(135deg,color-mix(in_srgb,var(--accent)_18%,var(--bg-2)),var(--bg-2))] p-3.5 shadow-[inset_0_0_0_1px_var(--line-soft)]">
        <div className="flex items-start gap-3">
          {me ? <Avatar user={me} size={44} /> : null}
          <div className="min-w-0 flex-1">
            <p className="m-0 flex items-baseline gap-2 text-[15px] text-ink">
              <b className="font-bold">{me?.displayName ?? 'A friend'}</b>
              <span className="ml-auto flex-none text-[12.5px] text-mute">Now</span>
            </p>
            <p className="m-0 text-[14.5px] text-ink">invited you to a party</p>
            <p className="m-0 mt-1 flex items-center gap-1.5 text-[13.5px] text-mute">
              <Icon name="gamepad" size={14} />
              {game ? "Let's play " + game.name : 'Squad up for a few games'}
            </p>
          </div>
        </div>
        <div className="mt-3 flex gap-2">
          <span className="flex h-10 flex-1 items-center justify-center rounded-[calc(12px*var(--radius-mul,1))] bg-accent text-[14px] font-bold text-accent-fg">Join party</span>
          <span className="flex h-10 flex-1 items-center justify-center rounded-[calc(12px*var(--radius-mul,1))] bg-surface-1 text-[14px] font-semibold text-dim shadow-[inset_0_0_0_1px_var(--line)]">Maybe later</span>
        </div>
      </div>

      <div className="mt-6 flex flex-col items-center text-center">
        <img src="./logo.png" alt="" width={110} height={110} className="h-[104px] w-[104px] object-contain drop-shadow-[0_10px_18px_rgba(0,0,0,.2)]" />
        <p className="mt-3 mb-0 max-w-[240px] text-[14px] leading-snug text-mute">
          Notifications keep you in the loop with your squad.
        </p>
      </div>
    </aside>
  );
}

/* ---------------------------------------------------- text and reading */

function TextTab() {
  const { settings, saveSettings } = useSession();
  const s = settings;
  if (!s) return null;
  const set = (patch: Record<string, unknown>) => void saveSettings(patch);

  /*
   * One press for the whole quiet look.
   *
   * Reduced motion, still pictures, no ambient wash and shallow shadows are
   * four switches in three places, and the people who want one of them
   * almost always want all four - on a laptop with a tired battery, on a
   * remote desktop, or because movement on a screen is unpleasant.
   */
  const lowOn = !!s.reduceMotion && s.gifAutoplay === false && s.illustration === false && (s.shadowDepth ?? 1) <= 0.5;
  const setLow = (on: boolean) => set(on
    ? { reduceMotion: true, gifAutoplay: false, illustration: false, shadowDepth: 0.5 }
    : { reduceMotion: false, gifAutoplay: true, illustration: true, shadowDepth: 1 });

  return (
    <>
      <SBlock title="Fewer effects" note="Motion, animated pictures, the background wash and depth, together.">
        <SRow label="Low effects" note="Sets reduced motion, still pictures, a plain background and flatter cards in one go">
          <SwitchC on={lowOn} onChange={setLow} />
        </SRow>
        <SRow label="Data saver" note="Small previews, and large pictures and video only when you ask for them">
          <SwitchC on={!!s.dataSaver} onChange={(v) => set({ dataSaver: v })} />
        </SRow>
        <SRow label="Controller and couch" note="Bigger buttons, and a focus ring you can see from the sofa">
          <SwitchC on={!!s.bigTargets} onChange={(v) => set({ bigTargets: v })} />
        </SRow>
      </SBlock>
      <SBlock title="Motion" note="How much the app moves.">
        <SRow label="Reduced motion" note="Stops animated frames, ripples and the theme fade">
          <SwitchC on={!!s.reduceMotion} onChange={(v) => set({ reduceMotion: v })} />
        </SRow>
        <SRow label="Play animated pictures" note="GIF avatars and emoji move without pointing at them">
          <SwitchC on={!!s.gifAutoplay} onChange={(v) => set({ gifAutoplay: v })} />
        </SRow>
      </SBlock>
      <SBlock title="Seeing clearly" note="App size and the typeface are under Appearance.">
        <SRow label="High contrast" note="Stronger borders and brighter secondary text">
          <SwitchC on={!!s.highContrast} onChange={(v) => set({ highContrast: v })} />
        </SRow>
      </SBlock>
    </>
  );
}

/* ==========================================================================
   Settings as a page
   ========================================================================== */

/**
 * Settings, as a screen rather than a panel over the app.
 *
 * It is a destination in the top bar now, so it gets the window like every
 * other destination: the section list is a column inside the page instead of
 * a rail inside a modal, and there is nothing to dismiss.
 */
/** Every section id, so a bad one in the address falls back instead of blanking. */
const TAB_IDS = new Set(GROUPS.flatMap((g) => g.items.map((i) => i.id as string)));

/** The heading over each section. Account and Notifications draw their own. */
const SECTION_HEAD: Partial<Record<string, [string, string]>> = {
  profile: ['Your profile', 'How you look to everyone else on Pugo.'],
  games: ['Games', 'The games on your profile, and how you play them.'],
  connections: ['Connections', 'Accounts you have linked, and what they show.'],
  premium: ['Membership', 'What your membership includes, and how to change it.'],
  privacy: ['Privacy & safety', 'Who can send you friend requests, messages and invites.'],
  voice: ['Voice & video', 'Your microphone, your headphones and how calls behave.'],
  text: ['Accessibility', 'Motion, contrast and moving pictures.'],
  keys: ['Keybinds', 'Shortcuts for voice, navigation and the view - simple to set, easy to change.'],
  chat: ['Chat', 'How messages look, and how chatting behaves.'],
  activity: ['Activity privacy', 'What others see about what you are doing, and what your status does.'],
  language: ['Language & time', 'The language Pugo speaks, and how times are shown.'],
  addons: ['Addons', 'Useful panels for Home - install from the library, or add your own. Every addon runs in a sandbox and cannot reach your account.'],
  advanced: ['Advanced', 'Updates, the desktop app, API keys and the connection.']
};

export function SettingsView() {
  /*
   * The section lives in the address: #/settings/profile opens profile
   * settings. That is what lets Edit profile land somewhere useful instead of
   * on whichever section happened to be first, and it means back works
   * between sections like it does everywhere else.
   */
  const route = useRoute();
  const asked = route.parts[1];
  const tab: Tab = (asked && TAB_IDS.has(asked) ? asked : 'account') as Tab;
  /*
   * Which of a section's own sections is open.
   *
   * In the address, so a link to "the font settings" is a link rather than
   * "open Appearance then press Size & font". Falls back to the first one
   * the section declares, so /settings/appearance still lands somewhere.
   */
  const subs = GROUPS.flatMap((g) => g.items).find((i) => i.id === tab)?.sub
    ?.filter((s) => !s.desktop || isDesktop());
  const askedPane = route.parts[2];
  const pane = subs && askedPane && subs.some((x) => x.id === askedPane) ? askedPane : (subs ? subs[0]!.id : '');
  const setPane = (next: string) => go('settings/' + tab + '/' + next);
  const setTab = (next: Tab) => go('settings/' + next);
  const narrow = useNarrow();
  /* Search the section names, so twelve rows do not have to be read through. */
  const [find, setFind] = useState('');
  const tr = useT();
  const q = find.trim().toLowerCase();
  const shownGroups = q
    ? GROUPS.map((g) => ({ ...g, items: g.items.filter((i) => (i.label + ' ' + tr('settings.' + i.id)).toLowerCase().includes(q)) })).filter((g) => g.items.length)
    /* GROUPS is typed now, so the cast that used to paper over the
       'as const' tuple is gone with it. */
    : GROUPS;

  return (
    <div className="min-h-0 flex-1">
      {/*
        On a phone this is an index or a section, never both.

        Stacked, the full category list came first and the section you had
        chosen started below it - #/settings/privacy put Privacy 1,167px down
        the page. Now the index is #/settings on its own, picking a category
        opens just that section at the top, and a back button returns.
      */}
      <div className={cn(
        'mx-auto flex h-full w-full max-w-[1560px] gap-6',
        narrow ? 'flex-col overflow-y-auto px-4 py-5' : 'px-7 py-6'
      )}>
        {/* The sections. Its own height, its own scrollbar if it ever needs
            one - it does not move when the section beside it does. */}
        <aside className={cn(
          'flex shrink-0 flex-col',
          narrow ? (asked ? 'hidden' : 'w-full') : 'w-[262px] min-h-0 overflow-y-auto'
        )}>
          <div className="mb-4">
            <h1 className="text-[26px] leading-tight font-extrabold tracking-tight">{tr('settings.title')}</h1>
            <label className="relative mt-3 block">
              <span className="sr-only">Search settings</span>
              <Icon name="search" size={16} className="pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2 text-mute" />
              <input
                type="search"
                value={find}
                onChange={(e) => setFind(e.target.value)}
                placeholder={tr('settings.search')}
                spellCheck={false}
                className="h-11 w-full rounded-[calc(12px*var(--radius-mul,1))] bg-surface-1 pr-3 pl-10 text-[14.5px] text-ink shadow-[inset_0_0_0_1px_var(--line)] outline-none transition-shadow duration-[250ms] placeholder:text-ghost focus:shadow-[inset_0_0_0_1px_var(--accent),0_0_0_3px_var(--accent-soft)]"
              />
            </label>
          </div>

          <nav className="pugo-panel rounded-[calc(16px*var(--radius-mul,1))] bg-surface-1 p-2 shadow-[inset_0_0_0_1px_var(--line-soft)]">
            {shownGroups.map((group, gi) => (
              <div key={group.label} className={cn(gi > 0 && 'mt-1 border-t border-line-soft pt-1')}>
                {group.items.map((t) => {
                  const TabIcon = t.icon;
                  const on = tab === t.id;
                  /* Only the ones this build actually has - see NavItem. */
                  const kids = t.sub?.filter((s) => !s.desktop || isDesktop());
                  /* Open only for the section you are on. Expanding every one
                     that has sub-sections turns a twelve-row sidebar into a
                     twenty-row one nobody can scan. */
                  const open = on && !!kids?.length;
                  const at = open ? Math.max(0, kids!.findIndex((s) => s.id === pane)) : 0;
                  return (
                    <div key={t.id}>
                      <button
                        type="button"
                        onClick={() => setTab(t.id as Tab)}
                        aria-current={on ? 'page' : undefined}
                        aria-expanded={kids?.length ? open : undefined}
                        className={cn(
                          'flex w-full items-center gap-2.5 rounded-[calc(10px*var(--radius-mul,1))] px-3 text-left font-semibold outline-none',
                          narrow ? 'h-12 text-[15px]' : 'h-[42px] text-[13.5px]',
                          'transition-[background-color,color,box-shadow] duration-[var(--t-hover)] ease-[var(--ease)]',
                          'focus-visible:shadow-[0_0_0_2px_var(--accent)]',
                          /* A soft tint with the accent on the text, as in the
                             Account reference - the solid fill shouted louder
                             than anything on the section beside it. */
                          on ? 'bg-accent-soft text-accent' : 'text-dim hover:bg-surface-2 hover:text-ink'
                        )}
                      >
                        <TabIcon size={16} className={on ? undefined : 'opacity-60'} />
                        <span className="min-w-0 flex-1 truncate">{tr('settings.' + t.id)}</span>
                        {kids?.length ? (
                          <Icon
                            name="chevronD"
                            size={13}
                            className={cn(
                              'flex-none transition-transform duration-[260ms] ease-[var(--ease-out)] motion-reduce:transition-none',
                              open ? 'rotate-180 opacity-80' : 'opacity-40'
                            )}
                          />
                        ) : null}
                      </button>

                      {/*
                        This section's own sections, directly under it.

                        Height rather than display, so opening one is a
                        movement and not a jump: a 0fr/1fr grid row is the
                        only way to animate to a height nobody has measured.
                        Closed, it is inert - a collapsed list still holds
                        focusable buttons otherwise, and tabbing lands you in
                        a menu that is not on screen.
                      */}
                      {kids?.length ? (
                        <div
                          className="grid transition-[grid-template-rows,opacity] duration-[260ms] ease-[var(--ease-out)] motion-reduce:transition-none"
                          style={{ gridTemplateRows: open ? '1fr' : '0fr', opacity: open ? 1 : 0 }}
                          inert={!open}
                        >
                          <div className="overflow-hidden">
                            <div className="relative mt-0.5 mb-1 ml-[19px] flex flex-col pl-2.5">
                              {/* The rail, and the mark that slides along it.
                                  One element that moves reads as the same
                                  thing changing place; a highlight that
                                  appears somewhere else reads as two. */}
                              <span aria-hidden className="absolute inset-y-1 left-0 w-[2px] rounded-full bg-line-soft" />
                              <span
                                aria-hidden
                                className="absolute left-0 h-[22px] w-[2px] rounded-full bg-accent transition-transform duration-[260ms] ease-[var(--ease-out)] motion-reduce:transition-none"
                                style={{ top: 6, transform: 'translateY(' + at * 32 + 'px)' }}
                              />
                              {kids!.map((sub) => {
                                const here = pane === sub.id;
                                return (
                                  <button
                                    key={sub.id}
                                    type="button"
                                    onClick={() => setPane(sub.id)}
                                    aria-current={here ? 'true' : undefined}
                                    className={cn(
                                      'flex h-8 flex-none items-center rounded-[calc(8px*var(--radius-mul,1))] px-2.5 text-left text-[13px] font-semibold outline-none',
                                      'transition-[background-color,color] duration-[var(--t-hover)] ease-[var(--ease)]',
                                      'focus-visible:shadow-[0_0_0_2px_var(--accent)]',
                                      here ? 'text-ink' : 'text-ghost hover:bg-surface-2 hover:text-dim'
                                    )}
                                  >
                                    {sub.label}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ))}
          </nav>
        </aside>

        {/* The section itself, scrolling on its own. */}
        <div
          key={tab}
          className={cn('panel-in min-w-0 flex-1', narrow ? (asked ? '' : 'hidden') : 'min-h-0 overflow-y-auto pr-1 pb-6')}
        >
          {narrow ? (
            <button
              type="button"
              onClick={() => go('settings')}
              className="mb-2 -ml-1 flex h-10 items-center gap-1 rounded-[calc(10px*var(--radius-mul,1))] pr-3 pl-1 text-[14px] font-semibold text-mute outline-none transition-colors duration-[var(--t-hover)] ease-[var(--ease)] hover:text-ink focus-visible:shadow-[0_0_0_2px_var(--accent)]"
            >
              <Icon name="chevronL" size={18} />
              All settings
            </button>
          ) : null}
          {/*
            The sub-sections again, for when the sidebar is not on screen.

            On a phone the sidebar is a separate screen, so the expanded list
            of sub-sections goes away with it and there is nothing left to
            press. A strip across the top is the same list in the shape that
            fits - and it is only ever one of the two, never both.
          */}
          {subs && narrow ? (
            <nav className="no-scrollbar -mx-1 mb-4 flex gap-1 overflow-x-auto px-1">
              {subs.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setPane(s.id)}
                  className={cn(
                    'flex-none rounded-full px-3 py-1.5 text-[13px] font-semibold whitespace-nowrap outline-none transition-colors duration-[var(--t-hover)] ease-[var(--ease)] focus-visible:shadow-[0_0_0_2px_var(--accent)]',
                    pane === s.id ? 'bg-accent-soft text-ink' : 'bg-surface-2 text-mute hover:text-dim'
                  )}
                >
                  {s.label}
                </button>
              ))}
            </nav>
          ) : null}
          {tab === 'appearance' ? (
            <AppearanceTab embedded pane={pane} onPane={setPane} />
          ) : (
            <div className={cn(
              '@container flex w-full flex-col gap-5',
              tab === 'notifications' || tab === 'account' ? 'max-w-[1180px]' : tab === 'profile' ? 'max-w-[1180px]' : 'max-w-[900px]'
            )}>
              {/* Account and Notifications carry their own heading. */}
              {SECTION_HEAD[tab] ? <PageHead title={SECTION_HEAD[tab]![0]} note={SECTION_HEAD[tab]![1]} /> : null}
              {tab === 'account' && <AccountTab />}
              {/*
                Profile is one column of settings with the answer beside it.

                It used to be two columns of forms with the result nowhere on
                the page - you set a banner, a colour and a font, then went to
                your own profile to find out what you had done. Every one of
                those is a look, and a look has to be looked at.
              */}
              {tab === 'profile' ? (
                <div className="flex flex-col items-start gap-5 @2xl:flex-row">
                  <div className="flex min-w-0 flex-1 flex-col gap-5"><ProfileTab /></div>
                  <aside
                    aria-label="How people see you"
                    className="w-full flex-none self-start @2xl:sticky @2xl:top-4 @2xl:w-[272px]"
                  >
                    <ProfilePreview />
                  </aside>
                </div>
              ) : null}
              {tab === 'games' && <GamesTab />}
              {tab === 'connections' && <ConnectionsTab />}
              {tab === 'premium' && <PremiumTab />}
              {tab === 'privacy' && <PrivacyTab />}
              {tab === 'voice' && <VoiceTab pane={pane} />}
              {tab === 'notifications' && <NotificationsTab />}
              {tab === 'text' && <TextTab />}
              {/*
                Chat, with the conversation beside it.

                Every setting on this tab is about reading a chat - the type
                size, the gap between messages, whether pictures are shown,
                how big an icon is drawn. "Changes show in the preview
                straight away" is only true if the preview is on screen, so
                it is pinned rather than somewhere further down the page.
              */}
              {tab === 'chat' ? (
                <div className="flex flex-col items-start gap-5 @2xl:flex-row">
                  <div className="flex min-w-0 flex-1 flex-col gap-5"><ChatTab /></div>
                  <aside
                    aria-label="Preview"
                    className="w-full flex-none self-start @2xl:sticky @2xl:top-4 @2xl:w-[300px]"
                  >
                    <ChatPreview onEmojiScale={(px) => void useSession.getState().saveSettings({ emojiScale: px })} />
                  </aside>
                </div>
              ) : null}
              {tab === 'activity' && <ActivityTab />}
              {tab === 'language' && <LanguageTab />}
              {tab === 'addons' && <AddonsTab />}
              {tab === 'keys' && <Keybinds />}
              {tab === 'advanced' && <ConnectionTab />}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Start with Windows.
 *
 * Windows is the authority here, not our own settings file: somebody can
 * switch Pugo off in Task Manager's Startup tab, and a checkbox that still
 * said "on" afterwards would be lying. The real state is read when the screen
 * opens and again whenever it is changed.
 */
function StartupToggle() {
  const [on, setOn] = useState(false);
  const [known, setKnown] = useState(false);

  useEffect(() => {
    const api = bridge();
    if (!api?.getStartup) return;
    void api.getStartup().then((s) => { setOn(!!s.enabled); setKnown(true); });
  }, []);

  const desktop = !!bridge()?.setStartup;
  if (!desktop) return null;

  return (
    <Toggle
      checked={on}
      disabled={!known}
      onChange={(next) => {
        setOn(next);
        void bridge()?.setStartup(next).then((real) => setOn(!!real));
        void useSession.getState().saveSettings({ launchOnStartup: next });
      }}
      label="Start Pugo when Windows starts"
      note="Opens straight into the tray - no window until you ask for one. Also listed under Startup in Task Manager."
    />
  );
}
